import ReactDOM from "react-dom"; // Add this line
import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import zoomPlugin from "chartjs-plugin-zoom";
import { MoreVertical, Copy, Maximize, Trash, Send, ChevronDown, Mic, Download, Square,
  RefreshCcw, Maximize2, Minimize2, FileText,Database, ThumbsUp, ThumbsDown, X,
  BookPlus, BookCheck,Edit, ChevronRight, CheckCircle, Layers,
  LayoutTemplate,
  Table,
  LineChart,
  BookOpen,
  AlertTriangle} from "lucide-react"; 
import { 
  downloadBubbleAsPDF, 
  attachChartDownloadDropdown, 
  downloadResponseAsPDF 
} from "./ChatDownload";

import {

  Chart,
  LineController,
  LineElement,
  BarController,
  BarElement,
  PieController,
  DoughnutController,
  BubbleController,
  PointElement,
  ArcElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  ScatterController,
  Filler
} from "chart.js";

import ChartDataLabels from 'chartjs-plugin-datalabels';
import DOMPurify from "dompurify";
import { motion, useReducedMotion } from "framer-motion";
import { marked } from "marked";
import { useNavigate, useLocation, useOutletContext } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./TokenDisplay.css";
import { useTheme } from "../../context/ThemeContext";

import "./ChatWindow.css";
import Logo from "../../assets/Genielytics.svg";
import api from "../../apiWithAccessToken";

import SkeletonLoader from "../ui/SkeletonLoader";
import Greeting from "./Greeting";
import KydPage from "../kyd/KydPage";
import FaqPopover from "./FaqPopover";
import OnboardingWizard from "../ChatWindow/OnboardingWizard"
import SuggestionCard, { SuggestionCardSkeleton } from "./suggestions/SuggestionsCard";
import DatabaseSelector, { DatabaseSelectorSkeleton } from "./DatabaseSelector";
import { renderAgentMarkdown } from "../../utils/renderAgentMarkdown";
import { readScopedValue, writeScopedValue } from "../../utils/scopedStorage";
import { getPendingSetupItems } from "../../utils/setupStatus";
import { getLlmProviderErrorMessage } from "../../utils/llmProviderError";
import { useChartValidator } from "./chartTypeValidator";
import ChartTypeSelector from "./ChartTypeSelector";
import { normalizeScrollBehavior } from "./scrollBehavior";
import {
  buildRenderableSections,
  chunkTextForStreaming,
  createLiveStreamState,
  getStreamingChunkDelay,
  isTextLikeStreamSection,
  STREAM_SECTION_GAP_MS,
} from "./responseStreaming";
import {
  buildQueryPayload,
  deriveActiveClarification,
  shapeAssistantClarificationFields,
} from "./clarificationUtils";
import {
  advanceQueryDeliveryState,
  appendAssistantMessageIfMissing,
  classifyQueryResponse,
  createQueryDeliveryState,
  extractQueryQuotaPayload,
  getQueryErrorMessage,
  isQueryDeliverySettled,
  mergeQueryStreamErrorPayload,
  mergeQueryTerminalPayload,
  shouldRenderPostResult,
  shouldRenderStreamFailure,
} from "./queryJobMode";
import {
  classifyInsightResponse,
  getInsightJobErrorMessage,
  mergeInsightStreamPayload,
} from "./insightJobMode";

  Chart.register(
  LineController,
  LineElement,
  BarController,
  BarElement,
  PieController,
  DoughnutController,
  BubbleController,
  PointElement,
  ArcElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  ScatterController,
  zoomPlugin,
  Filler,
  ChartDataLabels,
);

// Configure Markdown
marked.setOptions({
  breaks: true,
  highlight: (code, lang) => {
    try {
      return window.hljs && lang && window.hljs.getLanguage(lang)
        ? window.hljs.highlight(code, { language: lang }).value
        : window.hljs
        ? window.hljs.highlightAuto(code).value
        : code;
    } catch {
      return code;
    }
  },
});

const VOICE_LANGUAGES = [
  { code: "en-IN", label: "English (India)" },
  { code: "en-US", label: "English (US)" },
  { code: "hi-IN", label: "Hindi" },
  { code: "mr-IN", label: "Marathi" }, 
  { code: "gu-IN", label: "Gujarati" },
  { code: "ta-IN", label: "Tamil" },
  { code: "te-IN", label: "Telugu" },
  { code: "kn-IN", label: "Kannada" },
  { code: "ml-IN", label: "Malayalam" },
];

const WORKFLOW_MESSAGES = [
  "Synchronizing request with historical context...",
  "Mapping business entities to database structures...",
  "Architecting analytical execution plan...",
  "Translating requirements into optimized SQL...",
  "Interfacing with database engine for extraction...",
  "Validating result integrity against constraints...",
  "Extracting statistical patterns and core drivers...",
  "Configuring parameters for data visualization...",
  "Synthesizing narrative insights and components..."
];

const EXTENDED_MESSAGES = [
  "Navigating a particularly complex analytical path...",
  "Processing an extensive volume of data records...",
  "Running deep cross-checks for optimal precision...",
  "Finalizing the advanced data aggregation...",
  "This query requires deeper analysis, almost there...",
  "Optimizing the final output structure..."
];

const THINKING_MESSAGE_INTERVAL_MS = 4000;

const DOWNLOAD_DENIED_TOOLTIP =
  "Download access is denied for your account. If you think this is an error, please contact your admin.";

const INSIGHT_STATUS_VALUES = new Set([
  "not_requested",
  "generating",
  "completed",
  "failed",
]);

const normalizeInsightStatus = (value, { isEligible = false, insight = "" } = {}) => {
  if (String(insight || "").trim()) return "completed";
  const normalized = String(value || "").trim();
  if (INSIGHT_STATUS_VALUES.has(normalized)) return normalized;
  return isEligible ? "not_requested" : null;
};

const inferInsightEligibility = ({
  role,
  messageKind,
  insightStatus,
  sql,
  resultId,
  isDownloadable,
  chart,
}) => {
  if (role !== "bot") return false;
  if (messageKind === "chat_response") return false;
  if (messageKind === "query_result") return true;
  return Boolean(
    insightStatus ||
      sql ||
      resultId ||
      isDownloadable ||
      chart
  );
};

const getInsightErrorMessage = (error) => {
  const data = error?.response?.data || {};
  const statusText = data.status || "";

  if (statusText === "already_generating") {
    return "Insight generation is already in progress. Try again shortly.";
  }

  if (
    statusText === "result_artifact_unavailable" ||
    statusText === "missing_snapshot"
  ) {
    return "This result is too old to generate insights. Rerun the query.";
  }

  if (statusText === "generation_failed") {
    return data.error || "Insight generation failed. Please retry.";
  }

  return (
    data.error ||
    data.message ||
    error?.message ||
    "Unable to generate insights. Please try again."
  );
};

// ✅ UPDATED: Smart Date Formatter
const formatMonthYearLabel = (value) => {
  if (!value) return value;
  const strVal = String(value).trim();

  // Only format month-granularity labels like "2024-01" -> "Jan 2024".
  const monthOnlyMatch = strVal.match(/^(\d{4})-(\d{2})$/);
  if (monthOnlyMatch) {
    const year = Number(monthOnlyMatch[1]);
    const month = Number(monthOnlyMatch[2]);
    if (month >= 1 && month <= 12) {
      return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      });
    }
  }

  return value;
};


const formatIndianAmount = (value) => {
  if (value === null || value === undefined) return "";
  const raw = String(value).trim();
  const num = Number(raw);
  if (Number.isNaN(num)) return value;
  
  // ✅ REMOVED: The check for (num >= 1900 && num <= 2100) 
  // This ensures values like '2000' are formatted as '2 K' instead of raw '2000'

  const abs = Math.abs(num);
  if (abs >= 1.0e7) return parseFloat((num / 1.0e7).toFixed(2)) + " Cr";
  if (abs >= 1.0e5) return parseFloat((num / 1.0e5).toFixed(2)) + " L";
  if (abs >= 1.0e3) return parseFloat((num / 1.0e3).toFixed(2)) + " K";

  return parseFloat(num.toFixed(2));
};

// ✅ NEW: Smart Formatter for Data Labels (Value Only, No Unit Text)
const smartFormatNumber = (value) => {
  if (value === null || value === undefined) return "";
  const num = Number(value);
  if (Number.isNaN(num)) return value;

  const abs = Math.abs(num);
  // Auto-scale huge numbers but return ONLY the number
  if (abs >= 1.0e7) return parseFloat((num / 1.0e7).toFixed(2));
  if (abs >= 1.0e5) return parseFloat((num / 1.0e5).toFixed(2));
  if (abs >= 1.0e3) return parseFloat((num / 1.0e3).toFixed(2));

  // For small numbers (like percentages), return as is
  return parseFloat(num.toFixed(2));
};

const normalizeBubbleData = (seriesData) => {
  if (!seriesData || !seriesData.Data) return [];
  const rawData = seriesData.Data;
  const rValues = rawData.map(d => d.r);
  const minR = Math.min(...rValues);
  const maxR = Math.max(...rValues);
  
  if (minR === maxR) return rawData.map(d => ({ ...d, r: 10 }));

  const scale = (val) => {
    const minPx = 5, maxPx = 30;
    return minPx + ((val - minR) / (maxR - minR)) * (maxPx - minPx);
  };
  return rawData.map(d => ({ x: d.x, y: d.y, r: scale(d.r), _raw_r: d.r }));
};

const ChartRenderer = React.memo(({ chartData, agentType, sidebarCollapsed, responseId, theme, canDownload = true }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const containerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [selectedColorIdx, setSelectedColorIdx] = useState(null);
  const colorInputRef = useRef(null);
   const [editOverrides, setEditOverrides] = useState({
    chart_type: null,
    chart_title: null,
    x_label: null,
    y_label: null,
    is_stacked: null,
    is_area: null,
    colorPalette: null,   // custom palette override
    legendPosition: null, // 'top' | 'bottom' | 'left' | 'right'
    showDataLabels: null,
    borderRadius: null,   // for bars
    annotations:[],
    seriesLabels: {},
    _conversionError: null,
  _autoConvertTo: null, 
  });
  const validator = useChartValidator(chartData);
   const defaultPalette = [
    "#6366F1", "#10B981", "#F59E0B", "#3B82F6",
    "#EC4899", "#8B5CF6", "#06B6D4", "#F43F5E",
    "#84CC16", "#14B8A6", "#F97316", "#D946EF",
  ];

  const mergedChartData = useMemo(() => ({
    ...chartData,
    chart_type: editOverrides.chart_type || chartData.chart_type,
    meta: {
      ...chartData.meta,
      chart_title:   editOverrides.chart_title   ?? chartData.meta?.chart_title,
      x_label: editOverrides.x_label !== null ? editOverrides.x_label : (chartData.meta?.x_label ?? ""),
      y_label: editOverrides.y_label !== null ? editOverrides.y_label : (chartData.meta?.y_label ?? ""),
      is_stacked:    editOverrides.is_stacked     !==null ? editOverrides.is_stacked :( chartData.meta?.is_stacked ?? false),
      is_area:    editOverrides.is_area    !== null ? editOverrides.is_area    : (chartData.meta?.is_area    ?? false),
      legendPosition: editOverrides.legendPosition ?? 'top',
    }
  }), [chartData, editOverrides]);
  
  // 1. Helper: Determine Scale
  const getAxisScale = (dataValues) => {
    const validNums = dataValues.filter(n => typeof n === 'number' && !Number.isNaN(n));
    if (validNums.length === 0) return { divisor: 1, suffix: "" };

    const maxVal = Math.max(...validNums);
    const abs = Math.abs(maxVal);

    if (Number.isInteger(maxVal) && maxVal >= 1900 && maxVal <= 2100) return { divisor: 1, suffix: "" };
    if (abs >= 1.0e7) return { divisor: 1.0e7, suffix: " (Cr)" };
    if (abs >= 1.0e5) return { divisor: 1.0e5, suffix: " (L)" };
    if (abs >= 1.0e3) return { divisor: 1.0e3, suffix: " (K)" };

    return { divisor: 1, suffix: "" };
  };

  // 2. Calculate Scales (Updated for Dual Axis)
  const axesScales = useMemo(() => {
    const { chart_type, values, series, meta } = mergedChartData || {};
    // ── VALIDATION GUARD ──────────────────────────────────────────────────────
// If the user has selected an incompatible chart type, abort rendering
// and display a message rather than an empty/broken chart.
const validationResult = validator.validate(
  chart_type === "area" ? "line" : chart_type
);

if (!validationResult.valid) {
  // Clear any previous chart
  if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }

  // Draw a styled error placeholder on the canvas
  const ctx2 = canvasRef.current?.getContext("2d");
  if (ctx2) {
    const W = canvasRef.current.width;
    const H = canvasRef.current.height;
    ctx2.clearRect(0, 0, W, H);

    ctx2.fillStyle = theme === "dark" ? "rgba(30, 41, 59, 0.6)" : "rgba(248, 250, 252, 0.8)";
    ctx2.fillRect(0, 0, W, H);

    ctx2.fillStyle = theme === "dark" ? "#F59E0B" : "#D97706";
    ctx2.font = "bold 13px Inter, sans-serif";
    ctx2.textAlign = "center";
    ctx2.fillText("⚠ Incompatible chart type", W / 2, H / 2 - 14);

    ctx2.fillStyle = theme === "dark" ? "#94A3B8" : "#64748B";
    ctx2.font = "11px Inter, sans-serif";
    // Word-wrap the reason
    const words = validationResult.reason.split(" ");
    let line = "", lines = [], maxWidth = W * 0.75;
    for (const word of words) {
      const test = line + (line ? " " : "") + word;
      if (ctx2.measureText(test).width > maxWidth && line) {
        lines.push(line); line = word;
      } else { line = test; }
    }
    if (line) lines.push(line);
    lines.forEach((l, i) => ctx2.fillText(l, W / 2, H / 2 + 8 + i * 16));

    if (validationResult.autoConvertTo) {
      ctx2.fillStyle = theme === "dark" ? "#6366F1" : "#4F46E5";
      ctx2.font = "bold 11px Inter, sans-serif";
      ctx2.fillText(
        `Use the Edit panel to switch to ${
          validationResult.autoConvertTo.charAt(0).toUpperCase() +
          validationResult.autoConvertTo.slice(1)
        }`,
        W / 2, H / 2 + 8 + lines.length * 16 + 18
      );
    }
  }
  return; // <-- Skip normal chart construction
}
// ── END VALIDATION GUARD ─────────────────────────────────────────────────
    let xValues = [];
    let yValuesPrimary = [];
    let yValuesSecondary = [];

    // Helper to extract values from dataset
    const extractValues = (targetSeries) => {
      let vals = [];
      if (Array.isArray(targetSeries)) {
        targetSeries.forEach(v => {
           // Handle object data ({x,y}) or simple numbers
           if (typeof v === 'object' && v !== null && v.y !== undefined) vals.push(Number(v.y));
           else vals.push(Number(v));
        });
      }
      return vals;
    };

    if (series) {
      Object.entries(series).forEach(([key, arr]) => {
        // Check if this specific series is flagged as secondary in metadata
        const isSecondary = meta?.is_dual_axis && meta?.secondary_series_keys?.includes(key);
        
        if (isSecondary) {
            yValuesSecondary.push(...extractValues(arr));
        } else {
            yValuesPrimary.push(...extractValues(arr));
        }

        // Collect X values for bubble/scatter logic
        if (Array.isArray(arr)) {
           arr.forEach(v => {
             if (typeof v === 'object' && v !== null && v.x !== undefined) xValues.push(Number(v.x));
           });
        }
      });
    } else if (values) {
      yValuesPrimary = values.map(Number);
    }

    // fallback for empty secondary
    if (yValuesSecondary.length === 0) yValuesSecondary = [0];

    return { 
        x: getAxisScale(xValues), 
        y: getAxisScale(yValuesPrimary),
        y1: getAxisScale(yValuesSecondary) //  Right Axis Scale
    };
  }, [mergedChartData]);


  useEffect(() => {
    if (!canvasRef.current || !mergedChartData) return;
    if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }

    const ctx = canvasRef.current.getContext("2d");
    const isDarkTheme = theme === "dark";
    
    // Aesthetic Tweak: Slightly softer text and much softer grid lines for "Professional" look
    const chartTextPrimary = isDarkTheme ? "#F1F5F9" : "#1E293B"; 
    const chartTextSecondary = isDarkTheme ? "#94A3B8" : "#64748B";
    const chartGridColor = isDarkTheme ? "rgba(255, 255, 255, 0.06)" : "rgba(0, 0, 0, 0.06)";

    const THEME_COLORS = (editOverrides.colorPalette || defaultPalette).map(hex => ({
  border: hex,
  bg: hex
}));

    const { chart_type, labels = [], values = [], series, meta = {} } = mergedChartData;
    {
  const resolvedType = chart_type === "area" ? "line" : chart_type;
  const validationResult = validator.validate(resolvedType);
 
  if (!validationResult.valid) {
    // Destroy any previous chart instance
    if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }
 
    const canvas = canvasRef.current;
    if (canvas) {
      const dpr = window.devicePixelRatio || 1;
      const W   = canvas.offsetWidth  || canvas.width  / dpr;
      const H   = canvas.offsetHeight || canvas.height / dpr;
      const ctx2 = canvas.getContext("2d");
 
      ctx2.clearRect(0, 0, canvas.width, canvas.height);
 
      // Background tint
      ctx2.fillStyle = theme === "dark"
        ? "rgba(15, 23, 42, 0.5)"
        : "rgba(248, 250, 252, 0.85)";
      ctx2.fillRect(0, 0, canvas.width, canvas.height);
 
      // Scale for device pixel ratio so text is crisp
      ctx2.save();
      ctx2.scale(dpr, dpr);
 
      const cx = W / 2;
      const cy = H / 2;
 
      // Warning icon circle
      ctx2.beginPath();
      ctx2.arc(cx, cy - 38, 18, 0, Math.PI * 2);
      ctx2.fillStyle = theme === "dark" ? "rgba(245,158,11,0.15)" : "rgba(245,158,11,0.12)";
      ctx2.fill();
      ctx2.strokeStyle = theme === "dark" ? "#F59E0B" : "#D97706";
      ctx2.lineWidth = 1.5;
      ctx2.stroke();
 
      // "!" text
      ctx2.fillStyle  = theme === "dark" ? "#F59E0B" : "#D97706";
      ctx2.font       = "bold 18px Inter, sans-serif";
      ctx2.textAlign  = "center";
      ctx2.textBaseline = "middle";
      ctx2.fillText("!", cx, cy - 38);
 
      // Headline
      ctx2.fillStyle = theme === "dark" ? "#F1F5F9" : "#1E293B";
      ctx2.font      = "600 13px Inter, sans-serif";
      ctx2.fillText("Incompatible chart type", cx, cy - 10);
 
      // Reason — word-wrapped
      ctx2.fillStyle = theme === "dark" ? "#94A3B8" : "#64748B";
      ctx2.font      = "400 11px Inter, sans-serif";
 
      const maxWidth = Math.min(W * 0.75, 380);
      const words    = validationResult.reason.split(" ");
      let   line     = "";
      const lines    = [];
      for (const word of words) {
        const test = line + (line ? " " : "") + word;
        if (ctx2.measureText(test).width > maxWidth && line) {
          lines.push(line); line = word;
        } else {
          line = test;
        }
      }
      if (line) lines.push(line);
 
      lines.forEach((l, i) => ctx2.fillText(l, cx, cy + 10 + i * 16));
 
      // Hint line
      if (validationResult.autoConvertTo) {
        const hint = `Open Edit panel → switch to ${
          validationResult.autoConvertTo.charAt(0).toUpperCase() +
          validationResult.autoConvertTo.slice(1)
        }`;
        ctx2.fillStyle = theme === "dark" ? "#6366F1" : "#4F46E5";
        ctx2.font      = "500 10.5px Inter, sans-serif";
        ctx2.fillText(hint, cx, cy + 10 + lines.length * 16 + 20);
      }
 
      ctx2.restore();
    }
    return; // ← skip Chart.js construction entirely
  }
}
// ── END VALIDATION GUARD ─────────────────────────────────────────────────────
 
    const isStacked = meta.is_stacked === true;
    const isArea = meta.is_area === true;
    const isDualAxis = meta.is_dual_axis || false;
    const isScatter = chart_type === "scatter";
    const isBubble = chart_type === "bubble";
    const isPieOrDonut = chart_type === "pie" || chart_type === "doughnut";
    const isLinearX = isScatter || isBubble;
    const annotationLines = (editOverrides.annotations || []).filter(a => a.value !== "");

    let datasets = [];

    if (isBubble && series?.Data) {
      const bubbleData = normalizeBubbleData(series);
      datasets = [{
        label: meta.y_label || "Data",
        data: bubbleData,
        backgroundColor: "rgba(99, 102, 241, 0.6)", 
        borderColor: "#6366F1",
        borderWidth: 1.5
      }];
    } else if (series) {
     datasets = Object.entries(series).map(([label, sValues], i) => {
     const displayLabel = editOverrides.seriesLabels?.[label] || label;        const color = THEME_COLORS[i % THEME_COLORS.length];
        
        // Check if this series belongs to secondary axis
        const isSecondary = isDualAxis && meta?.secondary_series_keys?.includes(label);
        
        // Force line if it's a secondary axis or the main chart type is 'line'
        const forceLine = isSecondary; 
        const isLineChart = chart_type === 'line' || forceLine;

        let bg = color.bg;
        
        // Gradient for Area charts
        if (isArea && !isStacked && !isScatter && !forceLine) {
             const gradient = ctx.createLinearGradient(0, 0, 0, 400);
             gradient.addColorStop(0, color.bg + "90"); 
             gradient.addColorStop(1, color.bg + "05"); 
             bg = gradient;
        } else if (!isScatter && !forceLine) {
          bg = color.bg + "CC"; // Bars get opacity
        } else if (forceLine) {
          bg = color.bg; // Lines get solid points
        }

        return {
          label:displayLabel,
          data: sValues,
          borderColor: color.border,
          backgroundColor: isScatter ? color.border : bg,
          borderWidth: isLineChart ? 2.5 : 0,           // Ensure lines have width (2.5), while bars stay flat (0)
          tension: 0.35,
          fill: (isArea && !forceLine) ? 'origin' : false,
          pointRadius: (isScatter || forceLine) ? 3 : 0,       // Show points on Scatter, Secondary lines, or if it's a standard line chart with few points
          pointHoverRadius: 6,
          pointBackgroundColor: "#ffffff", 
          pointBorderWidth: 2,
          pointBorderColor: color.border,
          showLine: !isScatter,    
          yAxisID: isSecondary ? 'y1' : 'y',
          type: forceLine ? 'line' : undefined, 
          order: isSecondary ? 0 : 1 
        };
      });
    } else {
        // Single Series Logic
        const color = THEME_COLORS[0];
        const isLineChart = chart_type === 'line';
        
        let bg = color.bg + "CC";
        if (isArea) {
            const gradient = ctx.createLinearGradient(0, 0, 0, 300);
            gradient.addColorStop(0, color.bg + "90"); 
            gradient.addColorStop(1, color.bg + "05");
            bg = gradient;
        }
        datasets = [{
            label: editOverrides.seriesLabels?.["Value"] || meta.y_label || "Value",
            data: values,
            backgroundColor: chart_type.includes("pie") || chart_type.includes("doughnut") 
                ? THEME_COLORS.map(c => c.bg + "E6") 
                : bg,
            borderColor: chart_type.includes("pie") || chart_type.includes("doughnut") 
                ? (isDarkTheme ? "#1e293b" : "#ffffff") 
                : color.border,
            //  FIX: Ensure Lines and Pies have borders, Bars have 0
            borderWidth: (isLineChart || chart_type.includes("pie") || chart_type.includes("doughnut")) ? 2.5 : 0,
            tension: 0.35,
            fill: isArea ? "origin" : false,
            pointRadius: isLineChart ? 2 : 0, // Small points for single line chart
            pointHoverRadius: 6,
            pointBackgroundColor: "#ffffff",
            pointBorderWidth: 2,
        }];
    }
     console.log("DEBUG isStacked:", isStacked, "| meta.is_stacked:", meta.is_stacked, "| override:", editOverrides.is_stacked);
     // Polyfill for roundRect in older browsers
if (!CanvasRenderingContext2D.prototype.roundRect) {
  CanvasRenderingContext2D.prototype.roundRect = function(x, y, w, h, r) {
    this.beginPath();
    this.moveTo(x + r, y);
    this.lineTo(x + w - r, y);
    this.quadraticCurveTo(x + w, y, x + w, y + r);
    this.lineTo(x + w, y + h - r);
    this.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    this.lineTo(x + r, y + h);
    this.quadraticCurveTo(x, y + h, x, y + h - r);
    this.lineTo(x, y + r);
    this.quadraticCurveTo(x, y, x + r, y);
    this.closePath();
  };
}
    const manualAnnotationsPlugin = {
    id: "manualAnnotations",
    afterDraw(chart) {
    if (!annotationLines.length) return;
    const { ctx, chartArea, scales } = chart;
    if (!chartArea) return;

    ctx.save();

    annotationLines.forEach((ann) => {
      const color = ann.color || "#F43F5E";
      const isHorizontal = ann.type === "horizontal";

      let pos;
      if (isHorizontal) {
        const yScale = scales["y"];
        if (!yScale) return;
        const numVal = Number(ann.value);
        pos = yScale.getPixelForValue(isNaN(numVal) ? 0 : numVal);
      } else {
        const xScale = scales["x"];
        if (!xScale) return;
        const numVal = Number(ann.value);
        // For category axis use index, for linear use value
        if (xScale.type === "category") {
          const index = chart.data.labels?.indexOf(ann.value);
          pos = index !== -1 && index !== undefined
            ? xScale.getPixelForValue(index)
            : xScale.getPixelForValue(Number(ann.value));
        } else {
          pos = xScale.getPixelForValue(isNaN(numVal) ? 0 : numVal);
        }
      }

      if (pos === undefined || pos === null) return;

      // Draw dashed line
      ctx.beginPath();
      ctx.setLineDash([5, 4]);
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = color;

      if (isHorizontal) {
        ctx.moveTo(chartArea.left, pos);
        ctx.lineTo(chartArea.right, pos);
      } else {
        ctx.moveTo(pos, chartArea.top);
        ctx.lineTo(pos, chartArea.bottom);
      }
      ctx.stroke();
      ctx.setLineDash([]);

      // Draw label pill if provided
      if (ann.label && ann.label.trim()) {
        const text = ann.label.trim();
        ctx.font = "bold 10px Inter, sans-serif";
        const textWidth = ctx.measureText(text).width;
        const padX = 6, padY = 3;
        const boxW = textWidth + padX * 2;
        const boxH = 10 + padY * 2;

        let bx, by;
        if (isHorizontal) {
          bx = chartArea.right - boxW - 6;
          by = pos - boxH - 2;
        } else {
          bx = pos + 4;
          by = chartArea.top + 4;
        }

        // Pill background
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.roundRect(bx, by, boxW, boxH, 4);
        ctx.fill();

        // Label text
        ctx.fillStyle = "#ffffff";
        ctx.textBaseline = "middle";
        ctx.fillText(text, bx + padX, by + boxH / 2);
      }
    });

    ctx.restore();
  },
};
    chartRef.current = new Chart(ctx, {
      type: chart_type === "histogram" ? "bar" : chart_type,
      data: { labels: labels, datasets },
      plugins: [manualAnnotationsPlugin],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { top: 40, right: 20, left: 10, bottom: 10 } },
        interaction: {
          mode: 'index',
          intersect: false,
        },
        scales: {
           x: {
             stacked: isStacked,
             type: isLinearX ? 'linear' : 'category', 
             display: !chart_type.includes("pie") && !chart_type.includes("doughnut"),
             grid: { display: false, drawBorder: false },
             ticks: { 
               color: chartTextSecondary, 
               font: { size: 11, weight: "500", family: 'Inter' }, 
               maxRotation: 25,
               callback: function(value) {
                 if (isLinearX && typeof value === 'number') return formatIndianAmount(value);
                 if (this.getLabelForValue) return formatMonthYearLabel(this.getLabelForValue(value));
                 return value;
               }
             },
             title: {
               display: !!meta.x_label,
               text: meta.x_label ? `${meta.x_label}${axesScales.x.suffix}` : "",
               color: chartTextPrimary,
               font: { size: 12, weight: "600", family: 'Inter' },
               padding: { top: 10 }
             }
           },
           y: {
             type: 'linear',
             display: !chart_type.includes("pie") && !chart_type.includes("doughnut"),
             position: 'left',
             stacked: isStacked,
             grid: { color: chartGridColor, drawBorder: false, borderDash: [4, 4] }, // Dashed grid = modern
             ticks: {
               color: chartTextSecondary,
               font: { size: 11, weight: "500", family: 'Inter' },
               maxTicksLimit: 7,
               padding: 8,
               callback: (value) => {
                  if (axesScales.y.divisor > 1) return (value / axesScales.y.divisor).toFixed(1);
                  return formatIndianAmount(value);
               }
             },
             title: {
               display: !!meta.y_label,
               text: meta.y_label ? `${meta.y_label}${axesScales.y.suffix}` : "",
               color: chartTextPrimary,
               font: { size: 12, weight: "600", family: 'Inter' },
               padding: { bottom: 10 }
             }
           },
           y1: {
             type: 'linear',
             display: isDualAxis,
             position: 'right',
             grid: { drawOnChartArea: false },
             ticks: {
               color: chartTextSecondary,
               font: { size: 11, weight: "500", family: 'Inter' },
               maxTicksLimit: 7,
               padding: 8,
               callback: (value) => {
                  if (axesScales.y1.divisor > 1) return (value / axesScales.y1.divisor).toFixed(1);
                  return formatIndianAmount(value);
               }
             },
             title: {
               display: !!meta.secondary_y_label,
               text: meta.secondary_y_label ? `${meta.secondary_y_label}${axesScales.y1.suffix}` : "",
               color: chartTextPrimary,
               font: { size: 12, weight: "600", family: 'Inter' },
               padding: { bottom: 10 }
             }
           }
        },
        plugins: {
          legend: {
            display: editOverrides.legendPosition !== "none",
            position: editOverrides.legendPosition === "none"
              ? "top"
              : (editOverrides.legendPosition || "top"),
            align: "end",
            labels: { 
                usePointStyle: true, 
                boxWidth: 8, 
                padding: 15, 
                font: { size: 12, weight: "500", family: 'Inter' }, 
                color: chartTextSecondary 
            }
          },
          tooltip: {
             backgroundColor: isDarkTheme ? "rgba(15, 23, 42, 0.95)" : "rgba(255, 255, 255, 0.95)",
             titleColor: isDarkTheme ? "#F8FAFC" : "#0F172A",
             bodyColor: isDarkTheme ? "#F8FAFC" : "#0F172A",
             borderColor: isDarkTheme ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
             borderWidth: 1,
             padding: 12,
             cornerRadius: 8,
             usePointStyle: true,
             titleFont: { family: 'Inter', weight: '600' },
             bodyFont: { family: 'Inter' },
             callbacks: {
               label: (context) => {
                 let label = context.dataset.label || '';
                 if (label) label += ': ';
                 // Apply correct formatting based on which axis the dataset uses
                 const val = typeof context.parsed === 'object' && context.parsed !== null
                 ? context.parsed.y
                 : context.parsed;
                 
                 // If percentage logic needed, you could check label string here
                 // For now, use standard indian formatting
                 return label + formatIndianAmount(val);
               },
               title: (tooltipItems) => {
               return tooltipItems[0]?.label || '';
                }
             }
          },
          datalabels: {
               display: editOverrides.showDataLabels ?? false,
            color: chartTextPrimary,
            font: { size: 10, weight: "600", family: "Inter" },
            formatter: (value) => formatIndianAmount(value),
          },
          title: {
            display: !!meta.chart_title,
            text: meta.chart_title,
            font: { size: 16, weight: '700', family: 'Inter' },
            padding: { bottom: 20 },
            color: chartTextPrimary,
            align: "start"
          },
          zoom: {
             pan: { enabled: false },
             zoom: { wheel: { enabled: false }, drag: { enabled: true, backgroundColor: "rgba(99,102,241,0.2)" }, mode: "x" } 
          },
         
        }
      }
    });

    attachChartDownloadDropdown(
      containerRef.current,
      canvasRef.current,
      meta.chart_title || "Chart",
      responseId,
      { canDownload, deniedTooltip: DOWNLOAD_DENIED_TOOLTIP }
    );

    return () => { chartRef.current?.destroy(); };
  }, [mergedChartData, sidebarCollapsed, theme, canDownload, editOverrides]);

  const toggleFullscreen = () => { /* ... existing toggle ... */ };

  return (
    <div ref={containerRef} className="chart-container relative" style={{ height: "380px", width: "100%", position: "relative" }}>
       <div className="absolute top-2 right-12 flex items-center gap-2 z-10 chart-controls">
          <button className="chart-pill-btn" onClick={() => setShowEditPanel(true)}><Edit size={16} /></button>
          <button className="chart-pill-btn" onClick={() => chartRef.current?.resetZoom()}><RefreshCcw size={16} /></button>
          <button className="chart-pill-btn" onClick={() => { 
              if (!document.fullscreenElement) containerRef.current.requestFullscreen(); 
              else document.exitFullscreen(); 
          }}>
            <Maximize2 size={16} />
          </button>
       </div>
    {showEditPanel && (
  <div
    className="absolute top-0 right-0 h-full w-[200px] z-20 flex flex-col
               bg-white dark:bg-slate-900
               border-l border-slate-200 dark:border-slate-700
               rounded-r-lg shadow-xl"
    style={{ boxShadow: "-4px 0 24px rgba(0,0,0,0.10)" }}
  >
    {/* Header */}
    <div className="flex items-center justify-between px-3 py-2
                    border-b border-slate-100 dark:border-slate-800 flex-shrink-0">
      <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">
        Edit Chart
      </span>
      <button
        onClick={() => setShowEditPanel(false)}
        className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
      >
        <X size={14} />
      </button>
    </div>

    {/* Scrollable Body */}
    <div className="flex-1 overflow-y-auto px-3 py-3 space-y-4">

      {/* 1. Chart Type — with validation */}
      <ChartTypeSelector
        chartData={chartData}
        editOverrides={editOverrides}
        setEditOverrides={setEditOverrides}
        validator={validator}
      />

      {/* Divider */}
      <div className="h-px bg-slate-100 dark:bg-slate-800" />

      {/* 2. Options — contextual */}
      {(() => {
        const ct = editOverrides.chart_type || chartData.chart_type;
        const ia = editOverrides.is_area !== null
          ? editOverrides.is_area
          : (chartData.meta?.is_area ?? false);
        const effectiveType = (ct === "line" && ia) ? "area" : ct;
        const isPieDonut = effectiveType === "pie" || effectiveType === "doughnut";
        const isBarOrArea = effectiveType === "bar" || effectiveType === "area";
        const isLineOrArea = effectiveType === "line" || effectiveType === "area";

        const options = [
          //{ key: "is_stacked",     label: "Stacked",          show: isBarOrArea },
         // { key: "is_area",        label: "Area Fill",         show: isLineOrArea },
          { key: "showDataLabels", label: "Data Labels",       show: true },
        ].filter(o => o.show);

        if (options.length === 0) return null;

        return (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">
              Options
            </p>
            <div className="space-y-1">
              {options.map(({ key, label }) => (
                <label key={key}
                  className="flex items-center justify-between py-1 cursor-pointer group">
                  <span className="text-[11px] text-slate-600 dark:text-slate-300 group-hover:text-slate-800 dark:group-hover:text-white transition-colors">
                    {label}
                  </span>
                  <input
                    type="checkbox"
                    // ✅ FIX
                    checked={editOverrides[key] !== null ? editOverrides[key] : (chartData.meta?.[key] ?? false)}
                    onChange={e => setEditOverrides(p => ({ ...p, [key]: e.target.checked }))}
                    className="accent-indigo-600 w-3.5 h-3.5"
                  />
                </label>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Divider */}
      <div className="h-px bg-slate-100 dark:bg-slate-800" />

      {/* 3. Legend */}
      <div>
        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">
          Legend
        </p>
        <div className="grid grid-cols-3 gap-1">
          {["top","bottom","left","right","none"].map(pos => (
            <button
              key={pos}
              onClick={() => setEditOverrides(p => ({ ...p, legendPosition: pos }))}
              className={`py-1 rounded text-[10px] capitalize border transition-all
                ${(editOverrides.legendPosition ?? "top") === pos
                  ? "border-indigo-500 bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 font-semibold"
                  : "border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-indigo-300"
                }`}
            >
              {pos}
            </button>
          ))}
        </div>
      </div>

      {/* Divider */}
      <div className="h-px bg-slate-100 dark:bg-slate-800" />

      {/* 4. Color Palette */}
      {(() => {
        const activePalette = editOverrides.colorPalette || defaultPalette;
        const PRESETS = [
          { name: "Default", colors: ["#6366F1","#10B981","#F59E0B","#3B82F6","#EC4899","#8B5CF6"] },
          { name: "Cool",    colors: ["#3B82F6","#06B6D4","#8B5CF6","#60A5FA","#38BDF8","#818CF8"] },
          { name: "Warm",    colors: ["#F97316","#F43F5E","#F59E0B","#FB923C","#EF4444","#FBBF24"] },
          { name: "Nature",  colors: ["#10B981","#84CC16","#14B8A6","#22C55E","#4ADE80","#A3E635"] },
          { name: "Mono",    colors: ["#1E293B","#475569","#64748B","#94A3B8","#CBD5E1","#E2E8F0"] },
          { name: "Sunset",  colors: ["#F43F5E","#F97316","#F59E0B","#A855F7","#6366F1","#EC4899"] },
        ];

        const updateColor = (idx, hex) => {
          const updated = [...activePalette];
          updated[idx] = hex;
          setEditOverrides(p => ({ ...p, colorPalette: updated }));
        };

        const removeColor = (idx) => {
          if (activePalette.length <= 1) return;
          const updated = activePalette.filter((_, i) => i !== idx);
          setEditOverrides(p => ({ ...p, colorPalette: updated }));
          setSelectedColorIdx(null);
        };

        const addColor = () => {
          if (activePalette.length >= 12) return;
          const updated = [...activePalette, "#6366F1"];
          setEditOverrides(p => ({ ...p, colorPalette: updated }));
          setSelectedColorIdx(updated.length - 1);
        };

        const selectedColor = selectedColorIdx !== null ? activePalette[selectedColorIdx] : null;

        return (
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                Color Palette
              </p>
              {editOverrides.colorPalette && (
                <button
                  onClick={() => { setEditOverrides(p => ({ ...p, colorPalette: null })); setSelectedColorIdx(null); }}
                  className="text-[9px] text-slate-400 hover:text-rose-500 transition-colors"
                >
                  ↺ Reset
                </button>
              )}
            </div>

            {/* ── Preset themes (2-column grid) ── */}
            <div className="grid grid-cols-2 gap-1 mb-3">
              {PRESETS.map(preset => {
                const isActive = JSON.stringify(editOverrides.colorPalette) === JSON.stringify(preset.colors);
                return (
                  <button
                    key={preset.name}
                    onClick={() => { setEditOverrides(p => ({ ...p, colorPalette: preset.colors })); setSelectedColorIdx(null); }}
                    className={`flex flex-col items-start gap-1 px-2 py-1.5 rounded-lg border transition-all
                      ${isActive
                        ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20"
                        : "border-slate-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-700"
                      }`}
                  >
                    <div className="flex gap-0.5">
                      {preset.colors.slice(0, 6).map((c, i) => (
                        <div key={i} style={{ background: c }} className="w-2.5 h-2.5 rounded-sm flex-shrink-0" />
                      ))}
                    </div>
                    <span className={`text-[10px] font-medium ${isActive ? "text-indigo-600 dark:text-indigo-300" : "text-slate-500 dark:text-slate-400"}`}>
                      {preset.name}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* ── Divider with label ── */}
            <div className="flex items-center gap-2 mb-2.5">
              <div className="flex-1 h-px bg-slate-100 dark:bg-slate-800" />
              <span className="text-[9px] text-slate-400 uppercase tracking-widest flex-shrink-0">or custom</span>
              <div className="flex-1 h-px bg-slate-100 dark:bg-slate-800" />
            </div>

            {/* ── Active palette swatches ── */}
            <div className="flex flex-wrap gap-1.5 mb-2">
              {activePalette.map((color, idx) => {
                const isSelected = selectedColorIdx === idx;
                return (
                  <div key={idx} className="relative group/swatch">
                    {/* Swatch tile */}
                    <button
                      onClick={() => setSelectedColorIdx(isSelected ? null : idx)}
                      className={`w-7 h-7 rounded-lg transition-all flex-shrink-0
                        ${isSelected
                          ? "ring-2 ring-offset-1 ring-indigo-500 scale-110"
                          : "hover:scale-105 hover:ring-2 hover:ring-offset-1 hover:ring-slate-300 dark:hover:ring-slate-600"
                        }`}
                      style={{ background: color, boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }}
                      title={`Edit ${color}`}
                    />
                    {/* Remove button on hover */}
                    {activePalette.length > 1 && (
                      <button
                        onClick={() => removeColor(idx)}
                        className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 rounded-full bg-rose-500 text-white
                                   text-[8px] font-bold leading-none items-center justify-center
                                   opacity-0 group-hover/swatch:opacity-100 transition-opacity hidden group-hover/swatch:flex"
                        title="Remove color"
                      >×</button>
                    )}
                  </div>
                );
              })}
              {/* Add color slot */}
              {activePalette.length < 12 && (
                <button
                  onClick={addColor}
                  className="w-7 h-7 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600
                             flex items-center justify-center text-slate-400 hover:border-indigo-400
                             hover:text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20
                             transition-all text-sm font-bold flex-shrink-0"
                  title="Add a color"
                >+</button>
              )}
            </div>

            {/* ── Inline color editor for selected swatch ── */}
            {selectedColorIdx !== null && selectedColor && (
              <div className="mt-1 p-2 rounded-lg border border-indigo-200 dark:border-indigo-700
                              bg-indigo-50/60 dark:bg-indigo-900/15 space-y-2">
                <div className="flex items-center gap-2">
                  {/* Big color picker trigger */}
                  <label className="relative cursor-pointer flex-shrink-0">
                    <input
                      type="color"
                      value={selectedColor}
                      onChange={e => updateColor(selectedColorIdx, e.target.value)}
                      className="sr-only"
                      ref={colorInputRef}
                    />
                    <div
                      className="w-10 h-10 rounded-lg shadow-md cursor-pointer hover:scale-105 transition-transform border-2 border-white dark:border-slate-600"
                      style={{ background: selectedColor }}
                      onClick={() => colorInputRef.current?.click()}
                      title="Click to open color picker"
                    />
                    {/* Pencil overlay */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity rounded-lg bg-black/20">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                    </div>
                  </label>
                  {/* Hex input */}
                  <div className="flex-1 min-w-0">
                    <label className="text-[9px] text-slate-400 block mb-0.5">Hex value</label>
                    <input
                      type="text"
                      value={selectedColor}
                      maxLength={7}
                      onChange={e => {
                        const val = e.target.value;
                        if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) updateColor(selectedColorIdx, val);
                      }}
                      onBlur={e => {
                        if (!/^#[0-9A-Fa-f]{6}$/.test(e.target.value))
                          updateColor(selectedColorIdx, selectedColor);
                      }}
                      className="w-full px-2 py-1 text-[11px] font-mono rounded border
                                 border-indigo-200 dark:border-indigo-700
                                 bg-white dark:bg-slate-800
                                 text-slate-700 dark:text-slate-200
                                 focus:outline-none focus:border-indigo-500 transition-colors"
                      placeholder="#6366F1"
                    />
                  </div>
                  {/* Close editor */}
                  <button
                    onClick={() => setSelectedColorIdx(null)}
                    className="flex-shrink-0 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 mt-3"
                    title="Close"
                  >
                    <X size={13} />
                  </button>
                </div>

                {/* Quick color swatches for fast picking */}
                <div>
                  <p className="text-[9px] text-slate-400 mb-1">Quick pick</p>
                  <div className="flex flex-wrap gap-1">
                    {["#6366F1","#3B82F6","#10B981","#F59E0B","#F43F5E","#8B5CF6",
                      "#06B6D4","#F97316","#EC4899","#14B8A6","#84CC16","#EF4444",
                      "#1E293B","#64748B","#CBD5E1","#FFFFFF"].map(c => (
                      <button
                        key={c}
                        onClick={() => updateColor(selectedColorIdx, c)}
                        style={{ background: c, border: c === "#FFFFFF" ? "1px solid #e2e8f0" : "none" }}
                        className={`w-4 h-4 rounded-sm transition-all hover:scale-125
                          ${selectedColor === c ? "ring-2 ring-offset-1 ring-indigo-500 scale-110" : ""}`}
                        title={c}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}
      
    {/* Divider */}
      <div className="h-px bg-slate-100 dark:bg-slate-800" />

      {/* 5. Axis Labels */}
      {(() => {
        const ct = editOverrides.chart_type || chartData.chart_type;
        const isPieDonut = ct === "pie" || ct === "doughnut";
        if (isPieDonut) return null;

        return (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">
              Axis Labels
            </p>
            <div className="space-y-2">
              <div>
                <label className="text-[10px] text-slate-400 mb-0.5 block">X Axis</label>
                <input
                  type="text"
                  className="w-full px-2 py-1 text-[11px] rounded border
                             border-slate-200 dark:border-slate-700
                             bg-white dark:bg-slate-800
                             text-slate-700 dark:text-slate-200
                             placeholder:text-slate-300 dark:placeholder:text-slate-600
                             focus:outline-none focus:border-indigo-400 transition-colors"
                  placeholder={chartData.meta?.x_label || "X axis label"}
                  value={editOverrides.x_label ?? ""}
                  onChange={e => setEditOverrides(p => ({
                    ...p,
                    x_label: e.target.value === "" ? null : e.target.value
                  }))}
                />
              </div>
              <div>
                <label className="text-[10px] text-slate-400 mb-0.5 block">Y Axis</label>
                <input
                  type="text"
                  className="w-full px-2 py-1 text-[11px] rounded border
                             border-slate-200 dark:border-slate-700
                             bg-white dark:bg-slate-800
                             text-slate-700 dark:text-slate-200
                             placeholder:text-slate-300 dark:placeholder:text-slate-600
                             focus:outline-none focus:border-indigo-400 transition-colors"
                  placeholder={chartData.meta?.y_label || "Y axis label"}
                  value={editOverrides.y_label ?? ""}
                  onChange={e => setEditOverrides(p => ({
                    ...p,
                    y_label: e.target.value === "" ? null : e.target.value
                  }))}
                />
              </div>
            </div>
          </div>
        );
      })()}

      {/* Divider */}
      <div className="h-px bg-slate-100 dark:bg-slate-800" />

      {/* 6. Series / Legend Labels */}
      {(() => {
        const ct = editOverrides.chart_type || chartData.chart_type;
        const isPieDonut = ct === "pie" || ct === "doughnut";

        // Get series keys from chartData
        const seriesKeys = chartData.series
          ? Object.keys(chartData.series)
          : chartData.values
          ? ["Value"]
          : [];

        if (seriesKeys.length === 0) return null;

        return (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">
              Legend Labels
            </p>
            <div className="space-y-1.5">
              {seriesKeys.map((key) => (
                <div key={key}>
                  <label className="text-[9px] text-slate-400 block mb-0.5 truncate" title={key}>
                    {key}
                  </label>
                  <input
                    type="text"
                    className="w-full px-2 py-1 text-[11px] rounded border
                               border-slate-200 dark:border-slate-700
                               bg-white dark:bg-slate-800
                               text-slate-700 dark:text-slate-200
                               placeholder:text-slate-300 dark:placeholder:text-slate-600
                               focus:outline-none focus:border-indigo-400 transition-colors"
                    placeholder={key}
                    value={editOverrides.seriesLabels?.[key] || ""}
                    onChange={e => setEditOverrides(p => ({
                      ...p,
                      seriesLabels: {
                        ...p.seriesLabels,
                        [key]: e.target.value || undefined,
                      }
                    }))}
                  />
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      

   {/* Divider */}
      <div className="h-px bg-slate-100 dark:bg-slate-800" />

      {/* 6. Annotations */}
      {(() => {
        const ct = editOverrides.chart_type || chartData.chart_type;
        const isPieDonut = ct === "pie" || ct === "doughnut";
        if (isPieDonut) return null;

        const annotations = editOverrides.annotations || [];

        const addAnnotation = (type) => {
          const newAnnotation = {
            id: Date.now(),
            type, // 'horizontal' | 'vertical'
            value: "",
            label: "",
            color: "#F43F5E",
          };
          setEditOverrides(p => ({
            ...p,
            annotations: [...(p.annotations || []), newAnnotation],
          }));
        };

        const updateAnnotation = (id, field, val) => {
          setEditOverrides(p => ({
            ...p,
            annotations: (p.annotations || []).map(a =>
              a.id === id ? { ...a, [field]: val } : a
            ),
          }));
        };

        const removeAnnotation = (id) => {
          setEditOverrides(p => ({
            ...p,
            annotations: (p.annotations || []).filter(a => a.id !== id),
          }));
        };

        return (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">
              Annotations
            </p>

            {/* Add Buttons */}
            <div className="flex gap-1 mb-2">
              <button
                onClick={() => addAnnotation("horizontal")}
                className="flex-1 py-1 text-[10px] rounded border border-dashed
                           border-slate-300 dark:border-slate-600
                           text-slate-500 dark:text-slate-400
                           hover:border-indigo-400 hover:text-indigo-500
                           transition-colors"
              >
                + Horizontal
              </button>
              <button
                onClick={() => addAnnotation("vertical")}
                className="flex-1 py-1 text-[10px] rounded border border-dashed
                           border-slate-300 dark:border-slate-600
                           text-slate-500 dark:text-slate-400
                           hover:border-indigo-400 hover:text-indigo-500
                           transition-colors"
              >
                + Vertical
              </button>
            </div>

            {/* Annotation List */}
            {annotations.length === 0 && (
              <p className="text-[10px] text-slate-300 dark:text-slate-600 text-center py-1">
                No annotations yet
              </p>
            )}

            <div className="space-y-2">
              {annotations.map((ann) => (
                <div key={ann.id}
                  className="rounded border border-slate-200 dark:border-slate-700
                             bg-slate-50 dark:bg-slate-800/50 p-2 space-y-1.5">

                  {/* Header row: type badge + color + delete */}
                  <div className="flex items-center justify-between">
                    <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full
                      ${ann.type === "horizontal"
                        ? "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
                        : "bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400"
                      }`}>
                      {ann.type === "horizontal" ? "↔ H" : "↕ V"}
                    </span>
                    <div className="flex items-center gap-1.5">
                      {/* Color picker */}
                      <input
                        type="color"
                        value={ann.color}
                        onChange={e => updateAnnotation(ann.id, "color", e.target.value)}
                        className="w-5 h-5 rounded cursor-pointer border-0 bg-transparent"
                        title="Line color"
                      />
                      {/* Delete */}
                      <button
                        onClick={() => removeAnnotation(ann.id)}
                        className="text-slate-300 hover:text-rose-500 dark:text-slate-600
                                   dark:hover:text-rose-400 transition-colors"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  </div>

                  {/* Value input */}
                  <div>
                    <label className="text-[9px] text-slate-400 block mb-0.5">
                      {ann.type === "horizontal" ? "Y Value" : "X Value / Category"}
                    </label>
                    <input
                      type="text"
                      className="w-full px-2 py-1 text-[11px] rounded border
                                 border-slate-200 dark:border-slate-700
                                 bg-white dark:bg-slate-800
                                 text-slate-700 dark:text-slate-200
                                 placeholder:text-slate-300 dark:placeholder:text-slate-600
                                 focus:outline-none focus:border-indigo-400 transition-colors"
                      placeholder={ann.type === "horizontal" ? "e.g. 50000" : "e.g. Jan or 3"}
                      value={ann.value}
                      onChange={e => updateAnnotation(ann.id, "value", e.target.value)}
                    />
                  </div>

                  {/* Label input */}
                  <div>
                    <label className="text-[9px] text-slate-400 block mb-0.5">Label</label>
                    <input
                      type="text"
                      className="w-full px-2 py-1 text-[11px] rounded border
                                 border-slate-200 dark:border-slate-700
                                 bg-white dark:bg-slate-800
                                 text-slate-700 dark:text-slate-200
                                 placeholder:text-slate-300 dark:placeholder:text-slate-600
                                 focus:outline-none focus:border-indigo-400 transition-colors"
                      placeholder="e.g. Target, Launch..."
                      value={ann.label}
                      onChange={e => updateAnnotation(ann.id, "label", e.target.value)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

    </div>

    {/* Footer — Reset */}
    <div className="flex-shrink-0 px-3 py-2 border-t border-slate-100 dark:border-slate-800">  <button
        className="w-full py-1.5 text-[11px] text-slate-400 hover:text-rose-500
                   hover:bg-rose-50 dark:hover:bg-rose-900/10 rounded transition-colors"
        onClick={() => setEditOverrides({
          chart_type: null, chart_title: null, x_label: null, y_label: null,
          is_stacked: null, is_area: null, colorPalette: null,
          legendPosition: null, showDataLabels: null, borderRadius: null,annotations:[],
        })}
      >
        ↺ Reset to defaults
      </button>
    </div>
  </div>
)}

      <canvas ref={canvasRef} />
    </div>
  );
});


// ----------------------------------------------------------------------
// 4. INSTRUCTION MODAL (Engaging & Robust)
// ----------------------------------------------------------------------
const InstructionModal = ({ isOpen, onClose, data, onSave, loading, dbList = [] }) => {
  const [formData, setFormData] = useState({ guidance_text: "", tags: "", source_db_id: "" });
  const [viewState, setViewState] = useState("form"); // 'form' | 'saving' | 'success'

  useEffect(() => {
    if (isOpen && data) {
      // 1. Determine the best initial ID
      let targetId = data.source_db_id;
      
      // 2. SAFETY CHECK: If we have a list, ensure the targetId actually exists.
      // If the default '1' isn't in your list (e.g. you only have ID '5'), 
      // this snaps the form to the first valid ID immediately.
      if (dbList.length > 0) {
         const exists = dbList.some(db => Number(db.id) === Number(targetId));
         if (!exists) {
            targetId = dbList[0].id;
         }
      }

      setFormData({
        guidance_text: data.guidance_text || "",
        tags: data.tags || "",
        source_db_id: targetId // <--- Now uses the validated, safe ID
      });
      setViewState("form");
    }
  }, [isOpen, data, dbList]);

  const handleSubmit = async () => {
    setViewState("saving");
    
    // Combine original data with form inputs
    const payload = { ...data, ...formData };
    
    const success = await onSave(payload);
    
    if (success) {
      setViewState("success");
      // Close automatically after 1.5s
      setTimeout(() => {
        onClose();
        setViewState("form");
      }, 1500);
    } else {
      setViewState("form"); // Go back to form on error
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-[10002] flex items-center justify-center backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden border border-slate-200 dark:border-slate-700 flex flex-col max-h-[90vh] transition-all relative">
        
        {/* ================= LOADING / SUCCESS OVERLAY ================= */}
        {viewState !== "form" && (
           <div className="absolute inset-0 z-50 bg-white/90 dark:bg-slate-900/95 flex flex-col items-center justify-center text-center p-6">
              {viewState === "saving" && (
                 <div className="flex flex-col items-center animate-in zoom-in duration-300">
                    {/* Engaging Pulse Loader */}
                    <div className="relative w-16 h-16 mb-6">
                       <div className="absolute inset-0 border-4 border-indigo-200 dark:border-indigo-900 rounded-full"></div>
                       <div className="absolute inset-0 border-4 border-indigo-600 rounded-full border-t-transparent animate-spin"></div>
                       <Database className="absolute inset-0 m-auto text-indigo-600 animate-pulse" size={24} />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 dark:text-white mb-2">Ingesting Knowledge...</h3>
                    <p className="text-slate-500 dark:text-slate-400">Optimizing vector embeddings for future retrieval.</p>
                 </div>
              )}

              {viewState === "success" && (
                 <div className="flex flex-col items-center animate-in zoom-in duration-300">
                    <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mb-4 shadow-lg shadow-emerald-200 dark:shadow-none">
                       <BookCheck className="text-emerald-600 dark:text-emerald-400 w-8 h-8" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 dark:text-white mb-2">Saved to Knowledge Base!</h3>
                    <p className="text-slate-500 dark:text-slate-400">The AI will now use this context for future queries.</p>
                 </div>
              )}
           </div>
        )}

        {/* ================= STANDARD FORM HEADER ================= */}
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center bg-slate-50 dark:bg-slate-800/50">
          <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <BookPlus size={18} className="text-indigo-500" />
            Add Query Instruction
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* ================= FORM CONTENT ================= */}
        <div className="p-6 overflow-y-auto space-y-5">
           
           {/* 5. INSERT DATABASE SELECTOR HERE */}
           {dbList.length > 0 && (
             <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-100 dark:border-blue-800 flex items-center gap-3">
                <Database size={18} className="text-blue-600 dark:text-blue-400" />
                <div className="flex-1">
                   <label className="block text-xs font-bold text-blue-700 dark:text-blue-300 uppercase tracking-wider mb-1">
                     Target Database
                   </label>
                   <select 
                      className="w-full bg-transparent border-none p-0 text-sm font-semibold text-slate-800 dark:text-slate-100 focus:ring-0 cursor-pointer"
                      value={formData.source_db_id}
                      onChange={(e) => setFormData({ ...formData, source_db_id: Number(e.target.value) })}
                    >
                    {dbList.map(db => (
                      <option key={db.id} value={db.id} className="text-slate-800 bg-white">
                        {/* Update this line to use generic_name first */}
                        {db.generic_name || db.display_label || db.name} 
                      </option>
                    ))}
                  </select>
                </div>
                <ChevronDown size={16} className="text-blue-400" />
             </div>
           )}
           {/* Context Card */}
           <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
              <div className="mb-3">
                 <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">User Question</span>
                 <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mt-1">{data?.question_text}</p>
              </div>
              <div>
                 <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Generated SQL</span>
                 <div className="mt-1 p-2 bg-slate-950 rounded border border-slate-800 font-mono text-xs text-emerald-400 overflow-x-auto">
                    {data?.sql_query}
                 </div>
              </div>
           </div>

           {/* Input Section */}
           <div className="space-y-4">
              <div className="flex gap-3">
                 <div className="mt-1 flex-shrink-0 w-8 h-8 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                    <FileText size={16} />
                 </div>
                 {/* ✅ CHANGED: Added 'flex-1' so this container fills the width */}
                 <div className="flex-1">
                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200">
                       Why is this query correct?
                    </label>
                    <p className="text-xs text-slate-500 mb-2">Provide context to help the AI reuse this logic.</p>
                    <textarea 
                       className="w-full px-3 py-2 bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all text-sm min-h-[100px]"
                       placeholder="e.g. When calculating 'Risk', always filter by status='active'..."
                       value={formData.guidance_text}
                       onChange={(e) => setFormData({...formData, guidance_text: e.target.value})}
                       autoFocus
                    />
                 </div>
              </div>

              <div className="pl-11">
                 <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Tags</label>
                 <input 
                    type="text"
                    className="w-full px-3 py-2 bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all text-sm"
                    placeholder="finance, monthly-report, risk"
                    value={formData.tags}
                    onChange={(e) => setFormData({...formData, tags: e.target.value})}
                 />
              </div>
           </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 flex justify-end gap-3">
          <button 
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
          >
            Cancel
          </button>
          <button 
            onClick={handleSubmit}
            className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg shadow-sm shadow-indigo-200 dark:shadow-none transition-all flex items-center gap-2"
          >
            Save Instruction
          </button>
        </div>

      </div>
    </div>
  );
};


// ----------------------------------------------------------------------
// 5. FEEDBACK WIDGET (Fixed Visibility & Logic)
// ----------------------------------------------------------------------
const FeedbackWidget = ({ messageId, initialFeedback, onSubmit }) => {
  const [status, setStatus] = useState(initialFeedback?.score || 0); // 0, 1, -1
  const [showDropdown, setShowDropdown] = useState(false);
  const [view, setView] = useState("options"); // 'options' | 'custom'
  const [customText, setCustomText] = useState("");

  const PREDEFINED_ISSUES = [
    "Don't like the style",
    "Not factually correct",
    "Being lazy",
    "Formatting issues"
  ];

  const handleLike = () => {
    const newStatus = status === 1 ? 0 : 1;
    setStatus(newStatus);
    setShowDropdown(false);
    // Submit immediately
    onSubmit(messageId, newStatus, null, null);
  };

  const handleDislikeStart = () => {
    // If already disliked, toggle off
    if (status === -1) {
      setStatus(0);
      setShowDropdown(false);
      onSubmit(messageId, 0, null, null);
    } else {
      // Set status to -1 immediately so icon turns red
      setStatus(-1); 
      
      // Show dropdown for reason
      setShowDropdown(true); 
      setView("options");
      
      // Submit initial dislike immediately
      onSubmit(messageId, -1, null, null); 
    }
  };

  const handleReasonSelect = (reason) => {
    setStatus(-1);
    setShowDropdown(false);
    onSubmit(messageId, -1, reason, null);
  };

  const handleCustomSubmit = () => {
    if (!customText.trim()) return;
    setStatus(-1);
    setShowDropdown(false);
    onSubmit(messageId, -1, "Other", customText);
  };

  return (
    <div className="relative flex items-center gap-1">
      {/* Like Button */}
      <button 
        className={`p-1.5 rounded-md transition-colors ${
            status === 1 
                ? "text-emerald-500 bg-emerald-50 dark:bg-emerald-900/20" 
                : "text-slate-400 hover:text-emerald-600 hover:bg-slate-100 dark:text-slate-500 dark:hover:text-emerald-400 dark:hover:bg-slate-800"
        }`}
        onClick={handleLike}
        title="Good response"
      >
        <ThumbsUp 
          size={16} 
          fill={status === 1 ? "currentColor" : "none"} 
          strokeWidth={2}
        />
      </button>

      {/* Dislike Button */}
      <button 
        className={`p-1.5 rounded-md transition-colors ${
            status === -1 
                ? "text-rose-500 bg-rose-50 dark:bg-rose-900/20" 
                : "text-slate-400 hover:text-rose-600 hover:bg-slate-100 dark:text-slate-500 dark:hover:text-rose-400 dark:hover:bg-slate-800"
        }`}
        onClick={handleDislikeStart}
        title="Bad response"
      >
        <ThumbsDown 
          size={16} 
          fill={status === -1 ? "currentColor" : "none"} 
          strokeWidth={2}
        />
      </button>

      {/* Dislike Menu Dropdown */}
      {showDropdown && (
        <div className="absolute bottom-full left-0 mb-2 w-64 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              {view === "options" ? "What's the issue?" : "Provide details"}
            </span>
            <button 
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              onClick={() => setShowDropdown(false)}
            >
              <X size={14} />
            </button>
          </div>

          {view === "options" ? (
            <div className="p-1 flex flex-col gap-0.5">
              {PREDEFINED_ISSUES.map((issue) => (
                <button 
                  key={issue} 
                  className="w-full text-left px-3 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50 rounded-md transition-colors"
                  onClick={() => handleReasonSelect(issue)}
                >
                  {issue}
                </button>
              ))}
              <button 
                className="w-full text-left px-3 py-2 text-sm text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-md transition-colors font-medium"
                onClick={() => setView("custom")}
              >
                Other...
              </button>
            </div>
          ) : (
            <div className="p-3 flex flex-col gap-2">
              <textarea
                className="w-full p-2 text-sm border border-slate-200 dark:border-slate-700 rounded-md bg-transparent text-slate-800 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 min-h-[80px]"
                placeholder="Tell us what went wrong..."
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                autoFocus
              />
              <button 
                className="self-end px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium rounded-md transition-colors shadow-sm"
                onClick={handleCustomSubmit}
              >
                Submit Feedback
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
 
// 1.5 HELPER: Markdown Renderer

  const MarkdownRenderer = React.memo(({ content, renderMarkdown, className = "markdown-body" }) => {
    // If content is null/undefined, render nothing
    if (!content) return null;
    
    return (
      <div 
        className={className}
        dangerouslySetInnerHTML={renderMarkdown(content)}
      />
    );
  });

// ── Helper: Friendly label for knowledge type ──
const friendlyKnowledgeType = (rawType) => {
  if (!rawType) return "Business Rule";
  const map = {
    "SQL Expression":        "Custom Calculation",
    "sql_expression":        "Custom Calculation",
    "Business Metric":       "Business Metric",
    "business_metric":       "Business Metric",
    "Applied Business Metric": "Business Metric",
    "Filter":                "Data Filter",
    "filter":                "Data Filter",
    "Dimension":             "Data Dimension",
    "dimension":             "Data Dimension",
    "Aggregation":           "Aggregation Rule",
    "aggregation":           "Aggregation Rule",
  };
  return map[rawType] || rawType;
};

// ── Helper: Friendly label for knowledge name/sub-label ──
const friendlyKnowledgeName = (rawName) => {
  if (!rawName) return null;
  const map = {
    "Applied Business Metric": "Pre-defined business formula used",
    "Custom Aggregation":      "Count / Sum rule applied to data",
    "Business Context":        "Business definition applied",
  };
  return map[rawName] || rawName;
};

// ── MarketBuzzDrawer ── right-side sources panel (ChatGPT style) ───────────
const MBSRC_RE = /<!--MBSRC:([\s\S]*?)-->/;

/** Extract the <!--MBSRC:[...]-->  payload from insight markdown.
 *  Returns { cleanText, sources } where cleanText has the comment removed. */
function extractMBSources(text) {
  if (!text) return { cleanText: text, sources: [] };
  const m = text.match(MBSRC_RE);
  if (!m) return { cleanText: text, sources: [] };
  let sources = [];
  try { sources = JSON.parse(m[1]); } catch { sources = []; }
  return { cleanText: text.replace(MBSRC_RE, "").trimEnd(), sources };
}

const MarketBuzzDrawer = React.memo(({ sources, onClose }) => (
  <div className="mb-drawer-overlay" onClick={onClose}>
    <div className="mb-drawer" onClick={e => e.stopPropagation()}>

      {/* Header */}
      <div className="mb-drawer-header">
        <span className="mb-drawer-title">Sources</span>
        <button className="mb-drawer-close" onClick={onClose} title="Close">
          <X size={16} />
        </button>
      </div>

      {/* Source list */}
      <div className="mb-drawer-body">
        {sources.length === 0
          ? <div className="mb-drawer-empty">No sources available.</div>
          : sources.map((src, i) => {
              const domain = (() => {
                try { return new URL(src.url).hostname.replace(/^www\./, ""); }
                catch { return src.url; }
              })();
              return (
                <a key={i} href={src.url} target="_blank"
                   rel="noopener noreferrer" className="mb-source-item">
                  <div className="mb-source-meta">
                    {src.favicon_url
                      ? <img src={src.favicon_url} width={16} height={16}
                             className="mb-source-favicon" alt=""
                             onError={e => { e.currentTarget.style.display = "none"; }} />
                      : <div className="mb-source-favicon-fallback" />}
                    <span className="mb-source-domain">{domain}</span>
                  </div>
                  <div className="mb-source-title">{src.title || domain}</div>
                  {src.snippet && (
                    <div className="mb-source-snippet">
                      {src.snippet.length > 140
                        ? src.snippet.slice(0, 140) + "…"
                        : src.snippet}
                    </div>
                  )}
                </a>
              );
            })
        }
      </div>
    </div>
  </div>
));

// ── VerifiedSourcesDrawer: standalone collapsible panel ──
const VerifiedSourcesDrawer = React.memo(({
  activeSourcesMessage, onClose, sourceDbConfigId, user, dbList, renderMarkdown
}) => {
  const [openKnowledge, setOpenKnowledge] = useState({});
  const [sqlOpen, setSqlOpen] = useState(false);
  const [copiedSql, setCopiedSql] = useState(false);

  const toggleKnowledge = (idx) =>
    setOpenKnowledge(prev => ({ ...prev, [idx]: !prev[idx] }));

  const handleCopySql = () => {
    if (!activeSourcesMessage.sql) return;
    navigator.clipboard.writeText(activeSourcesMessage.sql);
    setCopiedSql(true);
    setTimeout(() => setCopiedSql(false), 1800);
  };

  const knowledgeList = activeSourcesMessage.sources?.knowledge || [];
  const tableList     = activeSourcesMessage.sources?.tables    || [];
  const hasSql        = !!activeSourcesMessage.sql;

  return (
    <div className="sources-drawer-overlay">
      <div className="sources-drawer" onClick={(e) => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="drawer-header">
          <div className="drawer-title-group">
            <div className="drawer-verified-badge">
              <CheckCircle size={13} />
            </div>
            <div>
              <h3 className="drawer-title-text">Verified Sources</h3>
              <p className="drawer-title-sub">AI-traced data lineage</p>
            </div>
          </div>
          <button className="drawer-close-btn" onClick={onClose} title="Close">
            <X size={16} />
          </button>
        </div>

        {/* ── Scrollable body ── */}
        <div className="drawer-content">

          {/* ── 1. Where did the data come from? ── */}
          <div className="drawer-section">
            <div className="drawer-section-label">
              <Database size={12} />
              <span>Where the data came from</span>
            </div>
            <div className="vs-db-card">
              <div className="vs-db-card-left">
                <div className="vs-db-icon-wrap">
                  <Database size={16} />
                </div>
                <div>
                <div className="vs-db-name">
                  {(() => {
                    const targetDbId = String(activeSourcesMessage.sourceDbId || sourceDbConfigId || user?.source_db_config_id);
                    const foundDb = dbList?.find(db => String(db.id) === targetDbId);
                    
                    // Update the return to prioritize generic_name
                    return foundDb?.generic_name || foundDb?.display_label || foundDb?.name || "Connected Database";
                  })()}
                </div>
                  <div className="vs-db-meta">Live database connection</div>
                </div>
              </div>
              <div className="vs-db-status">
                <span className="vs-status-pulse"></span>
                <span className="vs-status-label">Live</span>
              </div>
            </div>
          </div>

          {/* ── 2. Which tables were used? ── */}
          <div className="drawer-section">
            <div className="drawer-section-label">
              <Layers size={12} />
              <span>Tables used to answer your question</span>
              {tableList.length > 0 && (
                <span className="vs-count-badge">{tableList.length}</span>
              )}
            </div>

            {tableList.length > 0 ? (
              <div className="vs-tables-list">
                {tableList.map((t, idx) => (
                  <div key={idx} className="vs-table-row">
                    <div className="vs-table-icon">
                      <LayoutTemplate size={12} />
                    </div>
                    <span className="vs-table-name vs-table-name--full" title={t}>{t}</span>
                    <span className="vs-table-badge">used</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="vs-empty-state">
                <Table size={18} />
                <span>No specific tables identified</span>
              </div>
            )}
          </div>

          {/* ── 3. Business rules & definitions applied ── */}
          {knowledgeList.length > 0 && (
            <div className="drawer-section">
              <div className="drawer-section-label">
                <BookOpen size={12} />
                <span>Business rules &amp; definitions applied</span>
                <span className="vs-count-badge">{knowledgeList.length}</span>
              </div>
              <div className="vs-knowledge-list">
                {knowledgeList.map((k, idx) => {
                  const isOpen = !!openKnowledge[idx];
                  const label  = friendlyKnowledgeType(k.type);
                  const sub    = friendlyKnowledgeName(k.name);
                  const hasContent = !!k.content;
                  return (
                    <div key={idx} className={`vs-knowledge-card ${isOpen ? "vs-knowledge-card--open" : ""}`}>
                      <button
                        className="vs-knowledge-header vs-collapsible-trigger"
                        onClick={() => hasContent && toggleKnowledge(idx)}
                        aria-expanded={isOpen}
                        style={{ cursor: hasContent ? "pointer" : "default" }}
                      >
                        <div className="vs-knowledge-icon">
                          <BookCheck size={12} />
                        </div>
                        <div className="vs-knowledge-meta">
                          <span className="vs-knowledge-type">{label}</span>
                          {sub && <span className="vs-knowledge-name">{sub}</span>}
                        </div>
                        <span className="vs-applied-chip">Applied</span>
                        {hasContent && (
                          <ChevronDown size={13} className={`vs-chevron ${isOpen ? "vs-chevron--open" : ""}`} />
                        )}
                      </button>
                      {isOpen && hasContent && (
                        <div className="vs-knowledge-body markdown-body vs-collapsible-body">
                          <div dangerouslySetInnerHTML={renderMarkdown(k.content)} />
                        </div>
                      )}
                      {isOpen && !hasContent && (
                        <div className="vs-knowledge-body vs-knowledge-empty vs-collapsible-body">
                          No detailed content available.
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── 4. SQL Query used (shown if available) ── */}
          {hasSql && (
            <div className="drawer-section">
              <div className="drawer-section-label">
                <FileText size={12} />
                <span>Exact query run on your database</span>
              </div>
              <div className={`vs-sql-card ${sqlOpen ? "vs-sql-card--open" : ""}`}>
                <button
                  className="vs-sql-header vs-collapsible-trigger"
                  onClick={() => setSqlOpen(v => !v)}
                  aria-expanded={sqlOpen}
                >
                  <div className="vs-sql-icon">
                    <span className="vs-sql-dot"></span>
                    <span className="vs-sql-dot"></span>
                    <span className="vs-sql-dot"></span>
                  </div>
                  <div className="vs-sql-meta">
                    <span className="vs-sql-title">SQL Query</span>
                    <span className="vs-sql-sub">
                      {sqlOpen ? "Click to collapse" : "Click to view the generated query"}
                    </span>
                  </div>
                  <ChevronDown size={13} className={`vs-chevron ${sqlOpen ? "vs-chevron--open" : ""}`} />
                </button>

                {sqlOpen && (
                  <div className="vs-sql-body vs-collapsible-body">
                    <div className="vs-sql-copy-row">
                      <span className="vs-sql-lang-badge">SQL</span>
                      <button
                        className={`vs-sql-copy-btn ${copiedSql ? "vs-sql-copy-btn--copied" : ""}`}
                        onClick={handleCopySql}
                        title="Copy query"
                      >
                        {copiedSql ? (
                          <><CheckCircle size={11} /> Copied</>
                        ) : (
                          <><Copy size={11} /> Copy</>
                        )}
                      </button>
                    </div>
                    <pre className="vs-sql-code"><code>{activeSourcesMessage.sql}</code></pre>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Footer trust stamp ── */}
          <div className="vs-footer-stamp">
            <CheckCircle size={11} />
            <span>All sources verified and traceable</span>
          </div>

        </div>
      </div>
    </div>
  );
});

const BLOCK_REVEAL_ANIMATION = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.22, ease: "easeOut" },
};

const RevealBlock = ({ className, children }) => (
  <motion.div
    className={className}
    initial={BLOCK_REVEAL_ANIMATION.initial}
    animate={BLOCK_REVEAL_ANIMATION.animate}
    transition={BLOCK_REVEAL_ANIMATION.transition}
  >
    {children}
  </motion.div>
);

const StreamPlaceholder = ({ kind }) => (
  <div className={`stream-placeholder stream-placeholder--${kind}`} aria-hidden="true">
    <span className="stream-placeholder__glow" />
  </div>
);

const InsightSparklesIcon = ({ className = "" }) => (
  <svg
    className={`insight-sparkles-icon ${className}`}
    width="18"
    height="18"
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
  >
    <defs>
      <linearGradient id="insightSparkleMainGradient" x1="7" y1="3" x2="21" y2="18">
        <stop offset="0%" stopColor="#5fb1ff" />
        <stop offset="42%" stopColor="#7687ff" />
        <stop offset="72%" stopColor="#9a5cff" />
        <stop offset="100%" stopColor="#bd4cff" />
      </linearGradient>
      <linearGradient id="insightSparkleTopGradient" x1="2" y1="2" x2="8" y2="9">
        <stop offset="0%" stopColor="#2be4ff" />
        <stop offset="58%" stopColor="#25bdf4" />
        <stop offset="100%" stopColor="#42a8ff" />
      </linearGradient>
      <linearGradient id="insightSparkleSmallGradient" x1="3" y1="15" x2="9" y2="22">
        <stop offset="0%" stopColor="#6d8bff" />
        <stop offset="55%" stopColor="#8c63ff" />
        <stop offset="100%" stopColor="#b44cff" />
      </linearGradient>
    </defs>
    <path
      d="M13.5 3.2c.75 4.1 3.1 6.45 7.3 7.3-4.2.85-6.55 3.2-7.3 7.3-.85-4.1-3.2-6.45-7.3-7.3 4.1-.85 6.45-3.2 7.3-7.3Z"
      fill="url(#insightSparkleMainGradient)"
    />
    <path
      d="M5.2 2.7c.35 1.9 1.45 3 3.35 3.35-1.9.4-3 1.5-3.35 3.4-.4-1.9-1.5-3-3.4-3.4 1.9-.35 3-1.45 3.4-3.35Z"
      fill="url(#insightSparkleTopGradient)"
      opacity="0.9"
    />
    <path
      d="M6.1 15.2c.3 1.65 1.25 2.6 2.9 2.9-1.65.35-2.6 1.3-2.9 2.95-.35-1.65-1.3-2.6-2.95-2.95 1.65-.3 2.6-1.25 2.95-2.9Z"
      fill="url(#insightSparkleSmallGradient)"
      opacity="0.85"
    />
  </svg>
);

// 3. Message List Component (With Knowledge Instruction Feature)
const MessageList = React.memo(({ 
    messages, Logo, user, renderMarkdown, currentDbName, dbList, sourceDbConfigId,
    handleFollowUp, sidebarCollapsed, downloadingIds, handleDownload,
    onCSVDownload, onFeedback, theme, canDownload = true,
    onAddInstruction, onRemoveInstruction, isLoading, handleEditMessage,
    liveStreamStateByMessageId, onGenerateInsight,
}) => {
  const [openSql, setOpenSql] = useState({});
  const [activeSourcesMessage, setActiveSourcesMessage] = useState(null);
  const [activeBuzzSources, setActiveBuzzSources] = useState(null); // MB drawer
  const [openSources, setOpenSources] = useState({});
  const [copiedSqlId, setCopiedSqlId] = useState(null);
  const [speakingId, setSpeakingId] = useState(null);
  const [openClarificationPrompts, setOpenClarificationPrompts] = useState({});
  const [resultViewByKey, setResultViewByKey] = useState({});
  const speechRef = useRef(null);

  // close MB drawer on outside body-click
  useEffect(() => {
    if (activeBuzzSources) {
      document.body.classList.add("mb-drawer-open");
    } else {
      document.body.classList.remove("mb-drawer-open");
    }
    return () => document.body.classList.remove("mb-drawer-open");
  }, [activeBuzzSources]);

  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editDraft, setEditDraft] = useState("");
  useEffect(() => {
    if (activeSourcesMessage) {
      document.body.classList.add("sources-drawer-open");
    } else {
      document.body.classList.remove("sources-drawer-open");
    }
    return () => document.body.classList.remove("sources-drawer-open");
  }, [activeSourcesMessage]);

  // --- MENU STATE ---
  const [menuState, setMenuState] = useState({ 
      visible: false, x: 0, y: 0, 
      tableHtml: null, tableContainer: null, 
      message: null 
  });
  const [, forceUpdate] = useState(); 

  // 1. Close menu on fullscreen change
  useEffect(() => {
    const handleFullscreenChange = () => {
      setMenuState((prev) => ({ ...prev, visible: false }));
      forceUpdate({}); 
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // 2. Close menu on outside click AND scroll
  useEffect(() => {
    const closeMenu = () => setMenuState(prev => ({ ...prev, visible: false }));
    
    if (menuState.visible) {
      window.addEventListener("click", closeMenu);
      // capture: true is required because scroll events don't bubble up the DOM
      window.addEventListener("scroll", closeMenu, { capture: true }); 
    }
    
    return () => {
      window.removeEventListener("click", closeMenu);
      window.removeEventListener("scroll", closeMenu, { capture: true });
    };
  }, [menuState.visible]);
useEffect(() => {
  return () => {
    window.speechSynthesis?.cancel();
  };
}, []);
  const handleReadAloud = (message) => {
  const synth = window.speechSynthesis;

  // If already speaking this message — stop it
  if (speakingId === message.id) {
    synth.cancel();
    setSpeakingId(null);
    return;
  }

  // Stop any currently playing speech
  synth.cancel();

const parts = [];  // Collect all readable parts of the message


// 1. Main content or sql_response
if (message.sql_response) parts.push(message.sql_response);
else if (message.content) parts.push(message.content);

// 2. Insight block
if (message.insight) parts.push("Insights: " + message.insight);

// 3. Follow-up suggestions
if (message.followUps?.length > 0) {
  parts.push("Follow-up suggestions: " + message.followUps.join(". "));
}

const combined = parts.join("\n\n");

const cleanMarkdown = (text) => text
  .replace(/#{1,6}\s/g, "")                // headings
  .replace(/\*\*(.+?)\*\*/g, "$1")         // bold
  .replace(/\*(.+?)\*/g, "$1")             // italic
  .replace(/`{1,3}[^`]*`{1,3}/g, "")      // inline code / code blocks
  .replace(/\[(.+?)\]\(.+?\)/g, "$1")      // links
  .replace(/[-*+]\s/g, "")                 // bullets
  .replace(/\|.+\|/g, (match) => {         // table rows → readable
    return match
      .split("|")
      .map(cell => cell.trim())
      .filter(Boolean)
      .join(", ");
  })
  .replace(/[-|]+/g, "")                   // table separator lines
  .replace(/\n{3,}/g, "\n\n")              // collapse excess newlines
  .replace(/\n+/g, ". ")                   // newlines to speech pauses
  .replace(/\s{2,}/g, " ")                 // collapse extra spaces
  .trim();

const rawText = cleanMarkdown(combined);

  if (!rawText) return;

  const utterance = new SpeechSynthesisUtterance(rawText);
  utterance.lang = "en-IN";
  utterance.rate = 1;
  utterance.pitch = 1;

  utterance.onend = () => setSpeakingId(null);
  utterance.onerror = () => setSpeakingId(null);

  speechRef.current = utterance;
  setSpeakingId(message.id);
  synth.speak(utterance);
};

  // 3. Handle Table Option Clicks
  const handleContentClick = useCallback((e) => {
    const trigger = e.target.closest(".table-options-trigger");
    if (trigger) {
      e.stopPropagation();
      const rect = trigger.getBoundingClientRect();
      const container = trigger.closest(".table-container");
      
      const msgElement = trigger.closest(".message");
      const msgIdStr = msgElement?.id?.replace("msg-", "");
      const messageObj = messages.find((m) => String(m.id) === String(msgIdStr));

      if (container) {
        setMenuState({
          visible: true,
          x: rect.left - 160, 
          y: rect.bottom + 5,
          tableHtml: container.querySelector("table")?.outerHTML || "",
          tableContainer: container,
          message: messageObj 
        });
      }
    }
  }, [messages]);

  const portalTarget = document.fullscreenElement || document.body;
  const isTableFullscreen = menuState.tableContainer && document.fullscreenElement === menuState.tableContainer;

  // --- MENU CONTENT (Cleaned up - No Instruction Options) ---
  const MenuContent = (
    <div 
      className="table-options-menu"
      style={{ top: menuState.y, left: menuState.x, position: "fixed", zIndex: 10000 }}
      onClick={(e) => e.stopPropagation()} 
    >
      <button
        className={`table-menu-item ${!canDownload ? "disabled" : ""}`}
        disabled={!canDownload}
        title={!canDownload ? DOWNLOAD_DENIED_TOOLTIP : "Download CSV"}
        onClick={() => {
          if (!canDownload) return;
          if (menuState.message?.downloadId) {
            onCSVDownload(menuState.message.downloadId);
          } else {
            toast.error("Download is not available or has expired for this data.");
          }
          setMenuState(p => ({...p, visible: false}));
        }}
      >
  <FileText size={14} /> Download CSV
</button>
      
      <button className="table-menu-item" onClick={() => {
          if (menuState.tableHtml) {
            const blob = new Blob([menuState.tableHtml], { type: 'text/html' });
            const item = new ClipboardItem({ 'text/html': blob });
            navigator.clipboard.write([item]).then(() => toast.success("Table copied to clipboard!"));
          }
          setMenuState(p => ({...p, visible: false}));
      }}>
        <Copy size={14} /> Copy Table
      </button>

      {/* REMOVED INSTRUCTION BUTTONS FROM HERE */}

      <button className="table-menu-item" onClick={() => {
          const el = menuState.tableContainer;
          if (isTableFullscreen) {
            if (document.exitFullscreen) document.exitFullscreen();
          } else if (el && el.isConnected) {
             el.requestFullscreen?.();
          } else {
             toast.error("Table element lost. Try again.");
          }
          setMenuState(p => ({...p, visible: false}));
      }}>
        {isTableFullscreen ? <Minimize2 size={14} /> : <Maximize size={14} />} 
        {isTableFullscreen ? " Minimize" : " Fullscreen"}
      </button>
    </div>
  );

// --- HELPER: Render Action Footer ---
  const renderMessageFooter = (message) => {
    if (message.role !== "bot") return null;

    //  FIXED PERMISSION LOGIC
    // 1. Retrieve Role: Prioritize User Context, fallback to LocalStorage, default to empty
    const rawRole = user?.role || "";
    
    // 2. Normalize: Ensure case-insensitivity matches UserManagement (which uses lower case)
    const currentRole = String(rawRole).trim().toLowerCase();

    // 3. Define Allowed Roles: Strictly match values from UserManagement.jsx
    //    'super_admin' and 'admin' are the only ones allowed.
    const ADMIN_ROLES = ["super_admin", "admin"];

    // 4. Permission Flag: True ONLY if role is in the list
    const canManageKnowledge = ADMIN_ROLES.includes(currentRole);
    const isGreeting = message.content === "Hey I'm Geneilytics an AI assistant. How may I help you today!";

    const hasSources = message.sources && (message.sources.tables?.length > 0 || message.sources.knowledge?.length > 0);
    const isSourcesActive = activeSourcesMessage?.id === message.id;

    return (
      <div className="msg-footer-row">
        
        {/* Feedback Widget (Visible to everyone) */}
        {message.messageId && (
          <FeedbackWidget 
              messageId={message.messageId}
              initialFeedback={message.feedback} 
              onSubmit={onFeedback}
          />
        )}

        {/* Read Aloud Button */}
        {!isGreeting && (message.content || message.sql_response) && (
          <button
            className={`msg-footer-btn ${
              speakingId === message.id ? "msg-footer-btn--active" : ""
            }`}
            title={speakingId === message.id ? "Stop reading" : "Read aloud"}
            onClick={() => handleReadAloud(message)}
          >
            {speakingId === message.id ? (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                <line x1="23" y1="9" x2="23" y2="15" className="animate-pulse"/>
                <line x1="19" y1="7" x2="19" y2="17" className="animate-pulse"/>
              </svg>
            ) : (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
              </svg>
            )}
          </button>
        )}

        {/* Download Button */}
        <button
          className={`msg-footer-btn ${!canDownload ? "msg-footer-btn--disabled" : ""}`}
          title={canDownload ? "Download reply as PDF" : DOWNLOAD_DENIED_TOOLTIP}
          disabled={downloadingIds[message.id] || !canDownload}
          onClick={(e) => {
            e.preventDefault();
            if (!canDownload) return;
            handleDownload(message);
          }}
        >
          {downloadingIds[message.id] ? (
            <div className="spinner-loader" style={{
                width: "15px", height: "15px",
                border: "2px solid currentColor", borderTopColor: "transparent",
                borderRadius: "50%", animation: "spin 1s linear infinite"
            }} />
          ) : (
            <Download size={15} strokeWidth={2} />
          )}
        </button>

        {/* ── Divider before Sources/Knowledge ── */}
        {(hasSources || (message.sql && canManageKnowledge)) && (
          <div className="msg-footer-divider" />
        )}

        {/* ✅ FEATURE GATE: Knowledge base save — Admin/SuperAdmin only */}
        {message.sql && canManageKnowledge && (
           <button 
             className={`msg-footer-btn ${
                message.instructionId 
                  ? "msg-footer-btn--active" 
                  : ""
             }`}
             onClick={() => {
                if (message.instructionId) {
                   onRemoveInstruction(message);
                } else {
                   onAddInstruction(message);
                }
             }}
             title={message.instructionId ? "Saved to Knowledge Base" : "Save query to Knowledge Base"}
           >
             {message.instructionId ? (
                <BookCheck size={15} strokeWidth={2} />
             ) : (
                <BookPlus size={15} strokeWidth={2} />
             )}
           </button>
        )}

        {/* View Sources Button — LAST item, after add instruction */}
        {hasSources && (
          <button
            className={`msg-footer-sources-btn ${isSourcesActive ? "msg-footer-sources-btn--active" : ""}`}
            title="View verified data sources"
            onClick={(e) => {
              e.stopPropagation();
              setActiveSourcesMessage(prev => prev?.id === message.id ? null : message);
            }}
          >
            <Database size={13} className="msg-footer-sources-icon--db" />
            {message.sources.knowledge?.length > 0 && (
              <BookCheck size={13} className="msg-footer-sources-icon--bk" />
            )}
            <span className="msg-footer-sources-label">Sources</span>
            {isSourcesActive && <div className="msg-footer-sources-dot" />}
          </button>
        )}
      </div>
    );
  };

  const renderSqlDrawer = (message) => {
    if (!message.sql || !openSql[message.id]) return null;

    return (
      <div className="sql-drawer" style={{ marginTop: "10px" }}>
        <div className="sql-drawer-header">
          <span>Executed SQL Query</span>
          <button
            className={`copy-sql-btn ${copiedSqlId === message.id ? "copied" : ""}`}
            onClick={() => {
              navigator.clipboard.writeText(message.sql);
              setCopiedSqlId(message.id);
              setTimeout(() => setCopiedSqlId(null), 1500);
            }}
          >
            <span className="copy-label">Copy</span>
            <span className="copied-label">Copied ✓</span>
          </button>
        </div>
        <pre className="sql-code"><code>{message.sql}</code></pre>
      </div>
    );
  };

  const renderInsightSection = (insightText) => {
    const { cleanText, sources } = extractMBSources(insightText);
    const hasBuzzSrc = sources.length > 0;

    return (
      <div className="insight-box">
        <div className="insight-box__header">
          <InsightSparklesIcon />
          <strong><h1>Insight Result</h1></strong>
        </div>
        <div
          className="markdown-body agent-markdown"
          dangerouslySetInnerHTML={renderAgentMarkdown(cleanText, { wrapTables: true })}
        />
        {hasBuzzSrc && (
          <div className="mb-sources-strip">
            <span className="mb-sources-label">Sources</span>
            {sources.filter((src) => src.favicon_url).map((src, index) => (
              <button
                key={index}
                className="mb-favicon-btn"
                title={src.title || src.url}
                onClick={(event) => {
                  event.stopPropagation();
                  setActiveBuzzSources((prev) => (prev === sources ? null : sources));
                }}
              >
                <img
                  src={src.favicon_url}
                  width={16}
                  height={16}
                  alt=""
                  onError={(event) => { event.currentTarget.parentElement.style.display = "none"; }}
                />
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderInsightAction = (message) => {
    if (message.role !== "bot" || message.isError || !message.isInsightEligible) {
      return null;
    }

    if (message.insightStatus === "completed") {
      return null;
    }

    const isGenerating = message.insightStatus === "generating";
    const isFailed = message.insightStatus === "failed";
    const label = isGenerating
      ? "Generating insights..."
      : isFailed
      ? "Retry Insights"
      : "Generate Insights";

    return (
      <div className={`insight-action ${isFailed ? "insight-action--failed" : ""}`}>
        {message.insightError && (
          <div className="insight-action__error">{message.insightError}</div>
        )}
        <button
          type="button"
          className="generate-insights-btn"
          disabled={isGenerating || isLoading || !message.responseId}
          onClick={() => onGenerateInsight?.(message)}
          title={isGenerating ? "Insight generation is running" : label}
        >
          {isGenerating ? (
            <span className="generate-insights-btn__spinner" aria-hidden="true" />
          ) : (
            <span className="generate-insights-btn__icon-wrap">
              <InsightSparklesIcon />
            </span>
          )}
          <span className="generate-insights-btn__label" data-label={label}>{label}</span>
        </button>
      </div>
    );
  };

  const renderFollowUpsSection = (followUps) => (
    <RevealBlock className="followup-box">
      <strong>💬 Follow-up Suggestions:</strong>
      <div className="follow-up-container">
        {followUps.map((followUp, index) => (
          <button key={index} className="follow-up-btn" onClick={() => handleFollowUp(followUp)}>
            Q{index + 1}: {followUp}
          </button>
        ))}
      </div>
    </RevealBlock>
  );

  const renderFollowUpsLoading = (messageId) => (
    <RevealBlock key={`followups-loading-${messageId}`} className="followup-loading-box">
      <div className="followup-loading-box__header">
        <strong>💬 Follow-up Suggestions:</strong>
      </div>
      <div className="followup-loading-box__body">
        <span className="followup-loading-box__dot" aria-hidden="true" />
        <span className="followup-loading-box__label">Preparing follow-up suggestions...</span>
      </div>
    </RevealBlock>
  );

  const renderSqlToggleButton = (message, className = "") => (
      <button
        className={`sql-toggle-btn ${openSql[message.id] ? "is-open" : ""} ${className}`.trim()}
        onClick={() => setOpenSql((prev) => ({ ...prev, [message.id]: !prev[message.id] }))}
        aria-expanded={Boolean(openSql[message.id])}
      >
        {openSql[message.id] ? "Hide SQL" : "Show SQL"}
        <ChevronDown size={14} className={openSql[message.id] ? "rotate" : ""} />
      </button>
  );

  const renderResultControls = (message, { showSqlToggle = false } = {}) => {
    const insightAction = renderInsightAction(message);

    if (!insightAction && !showSqlToggle) return null;

    return (
      <div key={`result-controls-${message.id}`} className="result-controls-block">
        <div className="result-controls-row">
          <div className="result-controls-row__left">{insightAction}</div>
          <div className="result-controls-row__right">
            {showSqlToggle ? renderSqlToggleButton(message) : null}
          </div>
        </div>
        <div className="result-controls-divider" aria-hidden="true" />
      </div>
    );
  };

  const renderClarificationHistoryCard = (message) => {
    const question =
      message.clarificationRequest?.question ||
      message.pendingClarification?.clarification_question ||
      message.content ||
      message.sql_response ||
      "";

    const isOpen = Boolean(openClarificationPrompts[message.id]);

    return (
      <div className="clarification-history-card">
        <button
          type="button"
          className={`clarification-history-toggle ${isOpen ? "is-open" : ""}`}
          onClick={() =>
            setOpenClarificationPrompts((prev) => ({
              ...prev,
              [message.id]: !prev[message.id],
            }))
          }
          aria-expanded={isOpen}
        >
          <span className="clarification-history-toggle__label">Asked one clarification</span>
          <ChevronDown size={14} className="clarification-history-toggle__icon" />
        </button>

        {isOpen && question ? (
          <div className="clarification-history-question">
            {question}
          </div>
        ) : null}
      </div>
    );
  };

  const renderStreamSection = (message, section, streamState, sectionIndex) => {
    const targetSectionIds = streamState?.targetSectionIds;
    const hasExplicitTargets =
      Array.isArray(targetSectionIds) && targetSectionIds.length > 0;
    const isSectionTargeted =
      !hasExplicitTargets || targetSectionIds.includes(section.id);
    const isStreaming = Boolean(streamState && !streamState.completed && isSectionTargeted);
    const isActive = Boolean(
      isStreaming &&
        (streamState.activeSectionId
          ? streamState.activeSectionId === section.id
          : streamState.activeSectionIndex === sectionIndex)
    );
    const isVisible =
      !streamState ||
      !isSectionTargeted ||
      streamState.completed ||
      streamState.revealedSectionIds.includes(section.id) ||
      isActive;

    if (section.kind === "result") {
      if (!isVisible || section.pending) {
        return <StreamPlaceholder key={section.id} kind="table" />;
      }

      const viewKey = `${message.id}:${section.id}`;
      const currentView = resultViewByKey[viewKey] || section.defaultView || "table";
      const hasVizMode = Boolean(section.chart || section.chartPending || section.chartNotice);
      const activeView = hasVizMode ? currentView : "table";
      const nextView = activeView === "viz" ? "table" : "viz";

      return (
        <RevealBlock key={section.id} className="result-view-shell">
          <div className="result-view-header">
            <span className="result-view-label">Result</span>
            <div className="result-view-actions">
              {message.sql ? renderSqlToggleButton(message, "result-view-sql-toggle") : null}
              {hasVizMode && (
                <button
                  type="button"
                  className={`result-view-toggle ${activeView === "viz" ? "is-viz" : "is-table"}`}
                  onClick={() => setResultViewByKey((prev) => ({ ...prev, [viewKey]: nextView }))}
                  role="switch"
                  aria-checked={activeView === "viz"}
                  aria-label={`Switch to ${nextView === "viz" ? "visualization" : "table"} view`}
                  title={`Switch to ${nextView === "viz" ? "visualization" : "table"}`}
                >
                  <span
                    className={`result-view-toggle__item ${activeView === "table" ? "is-active" : ""}`}
                    aria-hidden="true"
                  >
                    <Table size={13} strokeWidth={2} />
                    <span>Table</span>
                  </span>
                  <span
                    className={`result-view-toggle__item ${activeView === "viz" ? "is-active" : ""}`}
                    aria-hidden="true"
                  >
                    <LineChart size={13} strokeWidth={2} />
                    <span>Viz</span>
                  </span>
                </button>
              )}
            </div>
          </div>
          {message.sql ? renderSqlDrawer(message) : null}
          <div className="result-view-body">
            {activeView === "viz" ? (
              section.chartNotice ? (
                <div className="chart-unavailable-notice" role="status">
                  <AlertTriangle size={14} aria-hidden="true" />
                  <span>{section.chartNotice}</span>
                </div>
              ) : section.chartPending || !section.chart ? (
                <StreamPlaceholder kind="chart" />
              ) : (
                <div className="chart-box">
                  <ChartRenderer
                    chartData={section.chart}
                    agentType={message.agentType}
                    sidebarCollapsed={sidebarCollapsed}
                    responseId={message.responseId}
                    theme={theme}
                    canDownload={canDownload}
                  />
                </div>
              )
            ) : (
              <div className="stream-table-shell">
                <MarkdownRenderer content={section.tableContent} renderMarkdown={renderMarkdown} />
              </div>
            )}
          </div>
        </RevealBlock>
      );
    }

    if (section.kind === "chart") {
      if (!isVisible || section.pending) {
        return <StreamPlaceholder key={section.id} kind="chart" />;
      }

      return (
        <RevealBlock key={section.id} className="chart-box">
          <ChartRenderer
            chartData={section.chart}
            agentType={message.agentType}
            sidebarCollapsed={sidebarCollapsed}
            responseId={message.responseId}
            theme={theme}
            canDownload={canDownload}
          />
        </RevealBlock>
      );
    }

    if (section.kind === "chartNotice") {
      if (!isVisible) return null;

      return (
        <RevealBlock key={section.id} className="chart-unavailable-notice" role="status">
          <AlertTriangle size={14} aria-hidden="true" />
          <span>{section.content}</span>
        </RevealBlock>
      );
    }

    if (section.kind === "table") {
      if (!isVisible || section.pending) {
        return isStreaming || section.pending
          ? <StreamPlaceholder key={section.id} kind="table" />
          : null;
      }

      return (
        <RevealBlock key={section.id} className="stream-table-shell">
          <MarkdownRenderer content={section.content} renderMarkdown={renderMarkdown} />
        </RevealBlock>
      );
    }

    if (section.kind === "insight") {
      if (!isVisible) return null;

      const activeText = streamState?.typedContentBySection?.[section.id];
      const content = streamState && !streamState.completed
        ? (activeText ?? "")
        : section.content;

      if (!String(content || "").trim()) return null;

      return (
        <RevealBlock key={section.id} className={`stream-text-block ${isActive ? "stream-text-block--active" : ""}`}>
          {renderInsightSection(content)}
          {isActive && (
            <span className="stream-inline-cursor" aria-hidden="true" />
          )}
        </RevealBlock>
      );
    }

    if (section.kind === "followUps") {
      if (section.pending) return renderFollowUpsLoading(message.id);
      if (!isVisible) return null;
      return <React.Fragment key={section.id}>{renderFollowUpsSection(section.followUps)}</React.Fragment>;
    }

    if (section.pending) {
      return <StreamPlaceholder key={section.id} kind="text" />;
    }

    if (!isVisible) return null;

    const activeText = streamState?.typedContentBySection?.[section.id];
    const content = streamState && !streamState.completed
      ? (activeText ?? "")
      : section.content;

    if (!String(content || "").trim()) return null;

    return (
      <div
        key={section.id}
        className={`stream-text-block ${isActive ? "stream-text-block--active" : ""}`}
      >
        <MarkdownRenderer content={content} renderMarkdown={renderMarkdown} />
        {isActive && (
          <span className="stream-inline-cursor" aria-hidden="true" />
        )}
      </div>
    );
  };

  const renderMessageBody = (message) => {
    const isClarificationHistoryMessage =
      message.role === "bot" &&
      message.pendingClarificationStatus === "pending" &&
      Boolean(
        message.clarificationRequest?.question ||
        message.pendingClarification?.clarification_question
      );

    if (isClarificationHistoryMessage) {
      return renderClarificationHistoryCard(message);
    }

    const sections = buildRenderableSections(message, message.ssePhase || null);
    const streamState = liveStreamStateByMessageId?.[message.id] || null;
    const isLiveStreaming = Boolean(streamState && !streamState.completed);
    const isSqlResponse = Boolean(message.sql || (message.isDownloadable && message.downloadId));
    const followUpsSectionIndex = sections.findIndex((section) => section.kind === "followUps");
    const followUpsSection =
      followUpsSectionIndex >= 0 ? sections[followUpsSectionIndex] : null;
    const followUpsTargetIds = streamState?.targetSectionIds;
    const isFollowUpsStreamTarget =
      followUpsSection &&
      (!Array.isArray(followUpsTargetIds) ||
        followUpsTargetIds.length === 0 ||
        followUpsTargetIds.includes(followUpsSection.id));
    const isFollowUpsVisible =
      !followUpsSection ||
      !streamState ||
      !isFollowUpsStreamTarget ||
      streamState.completed ||
      streamState.revealedSectionIds.includes(followUpsSection.id) ||
      (streamState.activeSectionId
        ? streamState.activeSectionId === followUpsSection.id
        : streamState.activeSectionIndex === followUpsSectionIndex);

    let sqlToggleInserted = false;
    let resultControlsInserted = false;
    const shouldShowInlineInsightAction =
      !isLiveStreaming ||
      (
        isLiveStreaming &&
        streamState.sections.length > 0 &&
        streamState.sections.every((section) => section.kind === "insight")
      );

    const contentNodes = sections.flatMap((section, sectionIndex) => {
      const nodes = [renderStreamSection(message, section, streamState, sectionIndex)];
      const targetSectionIds = streamState?.targetSectionIds;
      const hasExplicitTargets =
        Array.isArray(targetSectionIds) && targetSectionIds.length > 0;
      const isSectionTargeted =
        !hasExplicitTargets || targetSectionIds.includes(section.id);
      const isSectionVisible =
        !streamState ||
        !isSectionTargeted ||
        streamState.completed ||
        streamState.revealedSectionIds.includes(section.id) ||
        (streamState.activeSectionId
          ? streamState.activeSectionId === section.id
          : streamState.activeSectionIndex === sectionIndex);

      if (!sqlToggleInserted && message.sql && (section.kind === "result" || section.kind === "table") && isSectionVisible) {
        if (section.kind === "table") {
          const sqlControls = renderResultControls(message, { showSqlToggle: true });
          if (sqlControls) {
            nodes.push(sqlControls);
            resultControlsInserted = true;
          }
          if (!isLiveStreaming) {
            nodes.push(renderSqlDrawer(message));
          }
        }
        sqlToggleInserted = true;
      }

      if (
        !resultControlsInserted &&
        shouldShowInlineInsightAction &&
        section.kind === "followUps"
      ) {
        const insightControls = renderResultControls(message);
        if (insightControls) {
          nodes.unshift(insightControls);
          resultControlsInserted = true;
        }
      }

      return nodes;
    });

    if (!sqlToggleInserted && message.sql && !isLiveStreaming) {
      const sqlControls = renderResultControls(message, { showSqlToggle: true });
      if (sqlControls) {
        contentNodes.push(sqlControls);
        resultControlsInserted = true;
      }
      contentNodes.push(renderSqlDrawer(message));
    }

    if (followUpsSection && isLiveStreaming && !isFollowUpsVisible) {
      contentNodes.push(renderFollowUpsLoading(message.id));
    }

    if (!resultControlsInserted && shouldShowInlineInsightAction) {
      const insightControls = renderResultControls(message);
      if (insightControls) {
        contentNodes.push(insightControls);
      }
    }

    const content = <>{contentNodes}</>;

    if (isSqlResponse) {
      return <div className="sql-response-box">{content}</div>;
    }

    return content;
  };

  return (
    <>
      {menuState.visible && ReactDOM.createPortal(MenuContent, portalTarget)}

      {messages.map((message) => {
        const liveStreamState = liveStreamStateByMessageId?.[message.id] || null;
        const isMessageStreamComplete = !liveStreamState || liveStreamState.completed;

        return (
        <div key={message.id} id={`msg-${message.id}`} className={`message ${message.role}-message group`}>
          {message.role === "bot" ? (
            <img src={Logo} alt="Logo" className="avatar-img" />
          ) : (
            user?.avatar ? (
              <img src={user.avatar} alt="User avatar" className="avatar-img" />
            ) : (
              <div className="avatar-initials">
                {user.name?.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
              </div>
            )
          )}

          {/* NEW WRAPPER FOR BADGE & ICONS */}
          <div className="message-content-wrapper" style={{ alignItems: message.role === "user" ? "flex-end" : "flex-start" }}>
            
            {/* Edited Badge */}
            {message.role === "user" && message.is_edited && (
              <div className="edited-badge">Edited</div>
            )}

            {/* GEMINI-STYLE INLINE EDIT MODE — renders outside bubble, full width */}
            {message.role === "user" && editingMessageId === message.id && (
              <div className="gemini-edit-card">
                <textarea
                  className="gemini-edit-textarea"
                  value={editDraft}
                  onChange={(e) => {
                    setEditDraft(e.target.value);
                    e.target.style.height = "auto";
                    e.target.style.height = e.target.scrollHeight + "px";
                  }}
                  ref={(el) => {
                    if (el) {
                      el.style.height = "auto";
                      el.style.height = el.scrollHeight + "px";
                    }
                  }}
                  autoFocus
                />
                <div className="gemini-edit-actions">
                  <button onClick={() => setEditingMessageId(null)} className="gemini-edit-btn gemini-edit-cancel">Cancel</button>
                  <button
                    onClick={() => {
                      handleEditMessage(message.id, message.messageId || message.id, editDraft);
                      setEditingMessageId(null);
                    }}
                    disabled={editDraft.trim() === message.content.trim()}
                    className="gemini-edit-btn gemini-edit-update"
                  >
                    Update
                  </button>
                </div>
              </div>
            )}

            <div className="message-bubble-row" style={editingMessageId === message.id ? {display:"none"} : {}}>
              <div className={`bubble ${message.role === "bot" ? "response-bubble-with-info" : ""}`}>
            {message.role === "bot" && (
              <div className="response-info-icon-wrap" aria-label="Data protection information">
                <span className="response-info-icon" aria-hidden="true">i</span>
                <div className="response-info-tooltip" role="tooltip">
                  Genelytics protects your organizational data with enterprise-grade encryption while securely anonymizing natural language queries. Database schemas and metadata are processed locally where possible, and individual identifiers are removed from SQL execution logs.
                </div>
              </div>
            )}
                 {/* INLINE EDIT MODE (kept for structure, hidden via parent) */}
                 {false ? null : (
                    /* EXISTING BUBBLE CONTENT WRAPPER */
                    <div id={`main-content-${message.id}`} onClick={handleContentClick}>
                       {renderMessageBody(message)}
                    </div>
                 )}

                 {/* Follow Ups & Footer (Hide when editing) */}
                 {!editingMessageId && isMessageStreamComplete && (
                   <>
                     {renderMessageFooter(message)}
                   </>
                 )}
              </div>

            </div>

            {/* ACTION ICONS (Edit / Copy) — below the bubble */}
            {!editingMessageId && message.role === "user" && (
              <div className="user-action-bar">
                <button
                  className="p-1.5 text-slate-400 hover:text-indigo-500 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                  onClick={() => {
                    setEditingMessageId(message.id);
                    setEditDraft(message.content);
                  }}
                  disabled={isLoading}
                  title={isLoading ? "Please wait..." : "Edit query"}
                >
                  <Edit size={14} />
                </button>
                <button
                  className="p-1.5 text-slate-400 hover:text-indigo-500 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  onClick={() => {
                    navigator.clipboard.writeText(message.content);
                    toast.success("Query copied!");
                  }}
                  title="Copy query"
                >
                  <Copy size={14} />
                </button>
              </div>
            )}
          </div>
        </div>
      );
      })}
{/* ^^^ THIS IS THE ACTUAL END OF THE MESSAGES MAP ^^^ */}
      
      {/* === MARKET BUZZ SOURCES DRAWER === */}
      {activeBuzzSources && activeBuzzSources.length > 0 &&
        ReactDOM.createPortal(
          <MarketBuzzDrawer
            sources={activeBuzzSources}
            onClose={() => setActiveBuzzSources(null)}
          />,
          document.body
        )
      }

      {/* === VERIFIED SOURCES DRAWER (PRODUCTION ENHANCED) === */}
      {activeSourcesMessage && activeSourcesMessage.sources && ReactDOM.createPortal(
        <VerifiedSourcesDrawer
          activeSourcesMessage={activeSourcesMessage}
          onClose={() => setActiveSourcesMessage(null)}
          sourceDbConfigId={sourceDbConfigId}
          user={user}
          dbList={dbList}
          renderMarkdown={renderMarkdown}
          portalTarget={portalTarget}
        />,
        portalTarget
      )}
      {/* === END VERIFIED SOURCES DRAWER === */}

    </>
  );
});

// ----------------------------------------------------------------------
// 2️ MAIN COMPONENT
// ----------------------------------------------------------------------

const ChatWindow = ({ onLogout }) =>  {
  const {
    user,
    canDownload = false,
    sidebarCollapsed,
    activeSessionId,
    onActiveSessionChange,
    onSessionCreated,
    databases = [],
    selectedDatabaseId,
    setupStatus,
    llmRuntimeInfo,
    storageScope,
    onSelectDatabase,
    refreshCurrentUser,
  } = useOutletContext();
  const { theme } = useTheme();
  const prefersReducedMotion = useReducedMotion();
  const sourceDbConfigId = useMemo(() => {
    const parsedId = Number(selectedDatabaseId);
    return Number.isFinite(parsedId) && parsedId > 0 ? parsedId : null;
  }, [selectedDatabaseId]);
  const selectedDatabase = databases.find(db => Number(db.id) === sourceDbConfigId);
  const [faqRefreshBySource, setFaqRefreshBySource] = useState({});
  const mapRole = useCallback((role) => role === "assistant" ? "bot" : role, []);
  
  const [isSessionLoading, setIsSessionLoading] = useState(false);
  const [messagesMap, setMessagesMap] = useState({});
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [activeRequestKind, setActiveRequestKind] = useState(null);
  const [suppressedClarificationsBySession, setSuppressedClarificationsBySession] = useState({});
  const [thinkingMessage, setThinkingMessage] = useState(WORKFLOW_MESSAGES[0]);
  const [isThinkingMessageVisible, setIsThinkingMessageVisible] = useState(true);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [listening, setListening] = useState(false);
  const composerActivityClass = useMemo(() => {
    if (isLoading) return "composer--thinking";
    if (listening) return "composer--listening";
    if (inputValue.trim()) return "composer--typing";
    return "composer--idle";
  }, [inputValue, isLoading, listening]);
  
  //  States from both versions
  const [downloadingIds, setDownloadingIds] = useState({}); // From Fix
  const [currentResponseId, setCurrentResponseId] = useState(null); // From Peer
  const [liveStreamStateByMessageId, setLiveStreamStateByMessageId] = useState({});

  const navigate = useNavigate();
  const location = useLocation();
  const recognitionRef = useRef(null);
  
  const [showKydModal, setShowKydModal] = useState(false);
  const [kydData, setKydData] = useState(null);
  const [kydLoading, setKydLoading] = useState(false);
  const [kydCache, setKydCache] = useState({});
  const [showSetupModal, setShowSetupModal] = useState(false);

  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsEnabled, setSuggestionsEnabled] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(true);
  const [sessionId, setSessionId] = useState(
    () => activeSessionId || `temp_${Date.now()}`
  );

  const messages = useMemo(
    () => messagesMap[sessionId] || [],
    [messagesMap, sessionId]
  );
  const activeClarification = useMemo(
    () => deriveActiveClarification(messages),
    [messages]
  );
  const visibleActiveClarification = useMemo(() => {
    if (!activeClarification?.messageId) return activeClarification;
    const suppressedIds = suppressedClarificationsBySession[sessionId] || [];
    return suppressedIds.includes(activeClarification.messageId)
      ? null
      : activeClarification;
  }, [activeClarification, sessionId, suppressedClarificationsBySession]);
  const clarificationRequest = visibleActiveClarification?.clarificationRequest || null;
  const isClarificationMode = Boolean(visibleActiveClarification && clarificationRequest);
  const allowCustomClarification = clarificationRequest?.allow_custom_response === true;
  const allowDismissClarification = clarificationRequest?.allow_dismiss === true;
  const isInterruptibleRequest =
    activeRequestKind !== null && activeRequestKind !== "dismiss";
  const customClarificationValue = String(inputValue || "").trim();
  const canSubmitCustomClarification =
    allowCustomClarification && customClarificationValue.length > 0;
  const [activeSession, setActiveSession] = useState(null);
  const [currentTitle, setCurrentTitle] = useState("");
  const [isTransitioning, setIsTransitioning] = useState(false);

  const chatWindowRef = useRef(null);
  const textareaRef = useRef(null);
  const sessionIdRef = useRef(sessionId);
  const interruptedResponseIdsRef = useRef(new Set());
  const thinkingFadeTimeoutRef = useRef(null);
  const pinnedUserMessageIdRef = useRef(null);
  const prevMessagesLengthRef = useRef(0);
  const liveStreamTimersRef = useRef({});
  const liveStreamStateRef = useRef({});
  const sseReaderRef = useRef(null);
  const queryDeliveryByResponseIdRef = useRef({});
  const querySubmissionRef = useRef(null);
  const queryJobResponseIdsRef = useRef(new Set());
  const queryJobTerminalResponseIdsRef = useRef(new Set());
  const insightReadersByBaseResponseIdRef = useRef({});
  const isNearBottomRef = useRef(true);
  const scrollFrameRef = useRef(null);
  const manualStreamScrollOverrideRef = useRef(false);
  const liveRevealActiveRef = useRef(false);

  const advanceQueryDelivery = useCallback((responseId, event) => {
    const current =
      queryDeliveryByResponseIdRef.current[responseId] || createQueryDeliveryState();
    const next = advanceQueryDeliveryState(current, event);
    queryDeliveryByResponseIdRef.current[responseId] = next;
    return next;
  }, []);

  const clearSettledQueryDelivery = useCallback((responseId, delivery) => {
    if (isQueryDeliverySettled(delivery)) {
      delete queryDeliveryByResponseIdRef.current[responseId];
    }
  }, []);

  // --- KNOWLEDGE INSTRUCTION STATE ---
  const [instructionModalOpen, setInstructionModalOpen] = useState(false);
  const [currentInstruction, setCurrentInstruction] = useState(null); // The data being edited
  const [instructionLoading, setInstructionLoading] = useState(false);


  const [dbList, setDbList] = useState([]);
  // ─── SCOPED FIX: Only block querying if the *currently selected* DB
  // is not yet set up. Other already-configured databases must stay usable
  // even if a newly-added DB has incomplete setup.
  const isSetupIncomplete = useMemo(() => {
    // No DB selected yet → nothing to block
    if (!sourceDbConfigId) return false;

    // Check the per-DB flag from the databases list (is_setup_completed per entry)
    const selectedDb = databases.find(
      (db) => Number(db.id) === Number(sourceDbConfigId)
    );

    // If we found the DB entry and it's explicitly marked incomplete, block.
    // Fall back to the global flag only when the selected DB entry isn't in the list yet.
    if (selectedDb) {
      return selectedDb.is_setup_completed === false;
    }

    // DB not in list (edge case: just added, list not refreshed) → use global flag
    return !!(setupStatus && setupStatus.minimum_setup_complete === false);
  }, [sourceDbConfigId, databases, setupStatus]);
  const isTenantAdmin = String(user?.role || "").toLowerCase() === "admin";
  const getSourceDbIdForMessage = useCallback(
    (value) =>
      value ??
      sourceDbConfigId ??
      user?.source_db_config_id ??
      null,
    [sourceDbConfigId, user]
  );
  const buildAssistantMessageFromResponse = useCallback(
    (data, fallbackResponseId, fallbackSourceDbId) => {
      const clarificationFields = shapeAssistantClarificationFields(data);
      const chart = data.chart_data || null;
      const isInsightEligible = inferInsightEligibility({
        role: "bot",
        messageKind: data.message_kind,
        insightStatus: data.insight_status,
        sql: data.sql,
        resultId: data.result_id,
        isDownloadable: data.is_downloadable,
        chart,
      });
      const insight = data.insight || null;
      const insightStatus = normalizeInsightStatus(data.insight_status, {
        isEligible: isInsightEligible,
        insight,
      });
      const tableData = Array.isArray(data.table_data) ? data.table_data : [];

      return {
        id: Date.now() + Math.random(),
        messageId: data.message_id,
        role: "bot",
        status: data.status || null,
        partial: Boolean(data.partial),
        partialReason: data.partial_reason || null,
        chartStatus: data.chart_status || null,
        summaryStatus: data.summary_status || null,
        responseId: data.response_id || fallbackResponseId,
        sourceDbId: getSourceDbIdForMessage(
          data.source_db_id ??
            data.source_db_config_id ??
            fallbackSourceDbId
        ),
        sql: data.sql || null,
        sql_response: data.sql_response || null,
        insight,
        insightStatus,
        isInsightEligible,
        insightError: data.insight_error || null,
        tableData,
        followUps: data.follow_ups || [],
        chart,
        agentType: data.agent_type || null,
        sources: data.sources || null,
        isDownloadable: data.is_downloadable || false,
        downloadId: data.result_id || null,
        content:
          data.sql_response ||
          data.insight ||
          data.error ||
          "Sorry, I couldn’t understand that.",
        timestamp: new Date(),
        ...clarificationFields,
      };
    },
    [getSourceDbIdForMessage]
  );
  const markClarificationAsHandled = useCallback((sessionMessages, clarificationId, status) => {
    if (!clarificationId) return sessionMessages;

    return sessionMessages.map((message) =>
      message.id === clarificationId
        ? {
            ...message,
            requiresClarification: false,
            pendingClarificationStatus: status,
          }
        : message
    );
  }, []);
  const suppressClarificationForSession = useCallback((targetSessionId, clarificationId) => {
    if (!targetSessionId || !clarificationId) return;

    setSuppressedClarificationsBySession((prev) => {
      const currentIds = prev[targetSessionId] || [];
      if (currentIds.includes(clarificationId)) return prev;

      return {
        ...prev,
        [targetSessionId]: [...currentIds, clarificationId],
      };
    });
  }, []);
  const restoreClarificationForSession = useCallback((targetSessionId, clarificationId) => {
    if (!targetSessionId || !clarificationId) return;

    setSuppressedClarificationsBySession((prev) => {
      const currentIds = prev[targetSessionId] || [];
      if (!currentIds.includes(clarificationId)) return prev;

      const nextIds = currentIds.filter((id) => id !== clarificationId);
      if (nextIds.length === 0) {
        const { [targetSessionId]: _, ...rest } = prev;
        return rest;
      }

      return {
        ...prev,
        [targetSessionId]: nextIds,
      };
    });
  }, []);
  const pendingSetupItems = useMemo(() => {
    // Pass the selected DB id so getPendingSetupItems can scope
    // the pending items to *this* database only.
    return getPendingSetupItems(setupStatus, sourceDbConfigId);
  }, [setupStatus, sourceDbConfigId]);
  // 2. FETCH DB CONFIGS ON MOUNT
  // useEffect(() => {
  //   const fetchDBs = async () => {
  //     try {
  //       // Adjust endpoint if your API path differs (e.g. /db-config or /database-config)
  //       const { data } = await api.get("/db_config"); 
  //       setDbList(Array.isArray(data) ? data : []);
  //     } catch (err) {
  //       console.error("Failed to fetch DB configs for modal", err);
  //     }
  //   };
  //   fetchDBs();
  // }, []);

  // 1. Handle "Add to Instructions" Click -  OPENS MODAL ONLY (No API Call)
  const handleAddInstruction = (message) => {
    if (!message.sql) {
      toast.error("No SQL found to save.");
      return;
    }

    // 1. Find User Question Logic (Same as before)
    let userQuestionText = "User Question Not Found";
    const currentSessionMessages = messagesMap[sessionId] || [];
    const botMsgIndex = currentSessionMessages.findIndex(m => m.id === message.id);

    if (botMsgIndex !== -1) {
      for (let i = botMsgIndex - 1; i >= 0; i--) {
        if (currentSessionMessages[i].role === "user") {
          userQuestionText = currentSessionMessages[i].content;
          break;
        }
      }
    }
    
    // Fallback
    if (userQuestionText === "User Question Not Found" && message.relatedQuestion) {
        userQuestionText = message.relatedQuestion;
    }

    // ✅ Update to:
  const sourceDbId =
    message.sourceDbId ??
    sourceDbConfigId ??
    user?.source_db_config_id ??
    "";

    // 2. Prepare Data & Open Modal
    // We do NOT call API here. We just prepare the state.
    setCurrentInstruction({
      isNew: true, // Flag to tell modal this is a CREATE action
      messageIdRef: message.id, // Store ref to update UI later
      source_db_id: sourceDbId,
      question_text: userQuestionText,
      sql_query: message.sql,
      guidance_text: "",
      tags: ""
    });
    
    setInstructionModalOpen(true);
  };

  // 2. Handle "Save" from Modal - ✅ PERFORMS THE POST (Create or Update)
  const handleSaveInstruction = async (instructionData) => {
    try {
      setInstructionLoading(true);

      let responseData;
      
      if (instructionData.isNew) {
        // --- CREATE (POST) ---
        const payload = {
            source_db_id: instructionData.source_db_id,
            question_text: instructionData.question_text,
            sql_query: instructionData.sql_query,
            guidance_text: instructionData.guidance_text,
            tags: instructionData.tags,
            is_active: true
        };
        const { data } = await api.post("/query-instruction", payload);
        responseData = data.data; // Assuming backend returns { data: { id: ... } }
        
        // Update Message UI to show "Saved" icon
        setMessagesMap((prev) => {
            const newMap = { ...prev };
            const sessionMsgs = newMap[sessionId] || [];
            const msgIndex = sessionMsgs.findIndex(m => m.id === instructionData.messageIdRef);
            
            if (msgIndex !== -1) {
              sessionMsgs[msgIndex] = { 
                ...sessionMsgs[msgIndex], 
                instructionId: responseData.id 
              };
            }
            return newMap;
        });

      } else {
        // --- UPDATE (PUT) - For editing existing instructions ---
        const { data } = await api.put(`/query-instruction/${instructionData.id}`, {
            guidance_text: instructionData.guidance_text,
            tags: instructionData.tags
        });
        responseData = data.data;
      }
      
      // ✅ Return success to modal to trigger "Success" animation
      return true; 

    } catch (error) {
      console.error("Save failed", error);
      toast.error("Failed to save instruction.");
      return false;
    } finally {
      setInstructionLoading(false);
    }
  };

  // 2. Handle "Save Description" (PUT)
  const handleUpdateInstruction = async (id, formData) => {
    try {
      setInstructionLoading(true);
      await api.put(`/query-instruction/${id}`, formData);
      toast.success("Instruction updated successfully!");
      setInstructionModalOpen(false);
    } catch (error) {
      console.error("Update failed", error);
      toast.error("Failed to update details.");
    } finally {
      setInstructionLoading(false);
    }
  };

  // 3. Handle "Remove Instruction"
  const handleRemoveInstruction = async (message) => {
    if (!message.instructionId) return;
    
    try {
      await api.delete(`/query-instruction/${message.instructionId}`);
      toast.info("Instruction removed.");

      // Remove ID from local state
      setMessagesMap((prev) => {
        const newMap = { ...prev };
        const sessionMsgs = newMap[sessionId] || [];
        const msgIndex = sessionMsgs.findIndex(m => m.id === message.id);
        if (msgIndex !== -1) {
          const { instructionId, ...rest } = sessionMsgs[msgIndex];
          sessionMsgs[msgIndex] = rest;
        }
        return newMap;
      });
    } catch (error) {
      toast.error("Failed to remove instruction.");
    }
  };

  useEffect(() => {
    liveStreamStateRef.current = liveStreamStateByMessageId;
  }, [liveStreamStateByMessageId]);

  const clearLiveStreamTimers = useCallback((messageId = null) => {
    const timerMap = liveStreamTimersRef.current;
    const targetIds = messageId === null ? Object.keys(timerMap) : [String(messageId)];

    targetIds.forEach((targetId) => {
      (timerMap[targetId] || []).forEach((timerId) => window.clearTimeout(timerId));
      delete timerMap[targetId];
    });
  }, []);

  const finalizeLiveStream = useCallback((messageId) => {
    const targetId = String(messageId);
    clearLiveStreamTimers(targetId);

    setLiveStreamStateByMessageId((prev) => {
      const current = prev[targetId];
      if (!current) return prev;

      const typedContentBySection = {};
      current.sections.forEach((section) => {
        if (isTextLikeStreamSection(section)) {
          typedContentBySection[section.id] = section.content;
        }
      });

      return {
        ...prev,
        [targetId]: {
          ...current,
          status: "completed",
          activeSectionIndex: -1,
          activeSectionId: null,
          typedContentBySection,
          revealedSectionIds: current.sections.map((section) => section.id),
          completed: true,
        },
      };
    });
  }, [clearLiveStreamTimers]);

  const finalizeAllLiveStreams = useCallback(() => {
    Object.keys(liveStreamStateRef.current).forEach((messageId) => {
      const current = liveStreamStateRef.current[messageId];
      if (current && !current.completed) {
        finalizeLiveStream(messageId);
      }
    });
  }, [finalizeLiveStream]);

  const startLiveStream = useCallback((message, options = {}) => {
    if (!message || message.role !== "bot" || message.isError) return;

    const initialState = createLiveStreamState(message, options);
    if (initialState.completed) return;

    const messageId = String(message.id);
    const sections = initialState.sections;

    clearLiveStreamTimers(messageId);
    setLiveStreamStateByMessageId((prev) => ({ ...prev, [messageId]: initialState }));

    const addTimer = (callback, delay) => {
      const timerId = window.setTimeout(callback, delay);
      liveStreamTimersRef.current[messageId] = [
        ...(liveStreamTimersRef.current[messageId] || []),
        timerId,
      ];
      return timerId;
    };

    const updateMessageStream = (updater) => {
      setLiveStreamStateByMessageId((prev) => {
        const current = prev[messageId];
        if (!current) return prev;
        return {
          ...prev,
          [messageId]: updater(current),
        };
      });
    };

    const runSection = (sectionIndex) => {
      if (sectionIndex >= sections.length) {
        finalizeLiveStream(messageId);
        return;
      }

      const section = sections[sectionIndex];
      updateMessageStream((current) => ({
        ...current,
        activeSectionIndex: sectionIndex,
        activeSectionId: section.id,
        revealedSectionIds: Array.from(new Set([...current.revealedSectionIds, section.id])),
      }));

      const isTextLikeSection = isTextLikeStreamSection(section);

      if (!isTextLikeSection) {
        addTimer(() => runSection(sectionIndex + 1), STREAM_SECTION_GAP_MS);
        return;
      }

      if (prefersReducedMotion) {
        updateMessageStream((current) => ({
          ...current,
          typedContentBySection: {
            ...current.typedContentBySection,
            [section.id]: section.content,
          },
        }));
        addTimer(() => runSection(sectionIndex + 1), STREAM_SECTION_GAP_MS);
        return;
      }

      const chunks = chunkTextForStreaming(section.content);
      if (!chunks.length) {
        addTimer(() => runSection(sectionIndex + 1), STREAM_SECTION_GAP_MS);
        return;
      }

      let accumulated = "";

      const revealChunk = (chunkIndex) => {
        if (chunkIndex >= chunks.length) {
          addTimer(() => runSection(sectionIndex + 1), STREAM_SECTION_GAP_MS);
          return;
        }

        accumulated += chunks[chunkIndex];
        updateMessageStream((current) => ({
          ...current,
          typedContentBySection: {
            ...current.typedContentBySection,
            [section.id]: accumulated,
          },
        }));

        addTimer(() => revealChunk(chunkIndex + 1), getStreamingChunkDelay(chunks[chunkIndex]));
      };

      revealChunk(0);
    };

    runSection(0);
  }, [clearLiveStreamTimers, finalizeLiveStream, prefersReducedMotion]);

  const updateMessageInSession = useCallback((targetSessionId, targetMessageId, updater) => {
    if (!targetSessionId || targetMessageId === null || targetMessageId === undefined) return;

    setMessagesMap((prev) => {
      const sessionMsgs = prev[targetSessionId] || [];
      const msgIndex = sessionMsgs.findIndex(
        (message) => String(message.id) === String(targetMessageId)
      );
      if (msgIndex === -1) return prev;

      const nextSessionMsgs = [...sessionMsgs];
      const currentMessage = nextSessionMsgs[msgIndex];
      nextSessionMsgs[msgIndex] =
        typeof updater === "function"
          ? updater(currentMessage)
          : { ...currentMessage, ...updater };

      return { ...prev, [targetSessionId]: nextSessionMsgs };
    });
  }, []);

  const openInsightEventStream = useCallback((baseResponseId, targetSessionId, sourceDbId, targetMessageId) => {
    if (!baseResponseId || !targetSessionId || targetMessageId === null || targetMessageId === undefined) return;

    const existingReader = insightReadersByBaseResponseIdRef.current[baseResponseId];
    if (existingReader) {
      return;
    }

    const token = localStorage.getItem("access_token");
    const baseURL = api.defaults.baseURL || "";
    const url = `${baseURL}query/${baseResponseId}/insights/events`;

    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (response) => {
        if (!response.ok || response.status === 503) {
          updateMessageInSession(targetSessionId, targetMessageId, {
            insightStatus: "failed",
            insightError: "Unable to stream insights. Please retry.",
            isInsightEligible: true,
          });
          return;
        }

        const reader = response.body.getReader();
        insightReadersByBaseResponseIdRef.current[baseResponseId] = reader;
        const decoder = new TextDecoder();
        let buffer = "";

        const closeInsightStream = () => {
          reader.cancel().catch(() => {});
          if (insightReadersByBaseResponseIdRef.current[baseResponseId] === reader) {
            delete insightReadersByBaseResponseIdRef.current[baseResponseId];
          }
        };

        const applyInsightEvent = (eventName, data = {}) => {
          let messageForInsightStream = null;

          updateMessageInSession(targetSessionId, targetMessageId, (currentMessage) => {
            const merged = mergeInsightStreamPayload(
              {
                ...currentMessage,
                sourceDbId: currentMessage.sourceDbId ?? sourceDbId,
              },
              eventName,
              data
            );
            if (eventName === "insight_completed" && String(merged.insight || "").trim()) {
              messageForInsightStream = merged;
            }
            return merged;
          });

          if (messageForInsightStream) {
            startLiveStream(messageForInsightStream, { sectionKinds: ["insight"] });
          }

          if (eventName === "insight_completed" || eventName === "insight_failed") {
            closeInsightStream();
          }
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          const blocks = buffer.split("\n\n");
          buffer = blocks.pop() ?? "";
          for (const block of blocks) {
            if (!block.trim() || block.startsWith(": ")) continue;
            let event = null;
            let dataStr = null;
            for (const line of block.split("\n")) {
              if (line.startsWith("event: ")) event = line.slice(7).trim();
              else if (line.startsWith("data: ")) dataStr = line.slice(6).trim();
            }
            if (event && dataStr) {
              try {
                applyInsightEvent(event, JSON.parse(dataStr));
              } catch {}
            }
          }
        }
        if (insightReadersByBaseResponseIdRef.current[baseResponseId] === reader) {
          delete insightReadersByBaseResponseIdRef.current[baseResponseId];
        }
      })
      .catch(() => {
        if (insightReadersByBaseResponseIdRef.current[baseResponseId]) {
          delete insightReadersByBaseResponseIdRef.current[baseResponseId];
        }
        updateMessageInSession(targetSessionId, targetMessageId, {
          insightStatus: "failed",
          insightError: "Unable to stream insights. Please retry.",
          isInsightEligible: true,
        });
      });
  }, [startLiveStream, updateMessageInSession]);

  const handleGenerateInsight = useCallback(async (message) => {
    if (!message || message.insightStatus === "generating") return;

    const targetSessionId = sessionId;
    const sourceDbId =
      message.sourceDbId ??
      sourceDbConfigId ??
      user?.source_db_config_id ??
      null;

    if (!targetSessionId || !message.responseId || !sourceDbId) {
      toast.error("Unable to generate insights for this result.");
      return;
    }

    updateMessageInSession(targetSessionId, message.id, {
      insightStatus: "generating",
      insightError: null,
      isInsightEligible: true,
    });

    try {
      const insightResponse = await api.post("/query/insights", {
        base_response_id: message.responseId,
        session_id: targetSessionId,
        source_db_config_id: sourceDbId,
      });
      const { data } = insightResponse;
      const insightResponseKind = classifyInsightResponse(insightResponse);

      if (insightResponseKind.mode === "job_submitted") {
        updateMessageInSession(targetSessionId, message.id, (currentMessage) => ({
          ...currentMessage,
          insightStatus: insightResponseKind.insightStatus || "generating",
          insightError: null,
          isInsightEligible: true,
          sourceDbId: insightResponseKind.sourceDbConfigId ?? currentMessage.sourceDbId ?? sourceDbId,
        }));
        openInsightEventStream(
          insightResponseKind.baseResponseId || message.responseId,
          targetSessionId,
          insightResponseKind.sourceDbConfigId ?? sourceDbId,
          message.id
        );
        return;
      }

      if (insightResponseKind.mode === "already_generated") {
        const hasExistingInsight = String(message.insight || "").trim();
        updateMessageInSession(targetSessionId, message.id, (currentMessage) => ({
          ...currentMessage,
          messageId: insightResponseKind.messageId || currentMessage.messageId,
          insight: currentMessage.insight || null,
          insightStatus: hasExistingInsight ? "completed" : "generating",
          insightError: null,
          isInsightEligible: true,
          sourceDbId: insightResponseKind.sourceDbConfigId ?? currentMessage.sourceDbId ?? sourceDbId,
        }));

        if (!hasExistingInsight) {
          openInsightEventStream(
            insightResponseKind.baseResponseId || message.responseId,
            targetSessionId,
            insightResponseKind.sourceDbConfigId ?? sourceDbId,
            message.id
          );
        }
        return;
      }

      const returnedInsight = insightResponseKind.insight || data.insight || "";
      const nextStatus = normalizeInsightStatus(data.insight_status, {
        isEligible: true,
        insight: returnedInsight || message.insight,
      });
      const shouldStreamInsight =
        Boolean(returnedInsight) && !String(message.insight || "").trim();
      const messageForInsightStream = shouldStreamInsight
        ? {
            ...message,
            messageId: data.message_id || message.messageId,
            insight: returnedInsight,
            insightStatus: nextStatus,
            insightError: null,
            isInsightEligible: true,
          }
        : null;

      updateMessageInSession(targetSessionId, message.id, (currentMessage) => {
        return {
          ...currentMessage,
          messageId: data.message_id || currentMessage.messageId,
          insight: returnedInsight || currentMessage.insight || null,
          insightStatus: nextStatus,
          insightError: null,
          isInsightEligible: true,
        };
      });

      if (messageForInsightStream) {
        startLiveStream(messageForInsightStream, { sectionKinds: ["insight"] });
      }
    } catch (error) {
      console.error("Insight generation failed:", error);
      const errorMessage = getInsightJobErrorMessage(error) || getInsightErrorMessage(error);
      const responseData = error?.response?.data || {};

      updateMessageInSession(targetSessionId, message.id, {
        messageId: responseData.message_id || message.messageId,
        insightStatus: "failed",
        insightError: errorMessage,
        isInsightEligible: true,
      });
      toast.error(errorMessage);
    }
  }, [
    sessionId,
    openInsightEventStream,
    sourceDbConfigId,
    startLiveStream,
    updateMessageInSession,
    user?.source_db_config_id,
  ]);
  
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  const [voiceLang, setVoiceLang] = useState(
    () => readScopedValue(storageScope, "voice_lang") || "en-IN"
  );
  const queryModelSelection = llmRuntimeInfo?.query_model_selection || {};
  const queryModelOptions = useMemo(
    () => (Array.isArray(queryModelSelection.models) ? queryModelSelection.models : []),
    [queryModelSelection.models]
  );
  const isQueryModelSelectionEnabled =
    queryModelSelection.enabled === true && queryModelOptions.length > 0;
  const defaultPrimaryModel =
    queryModelSelection.default_model || queryModelOptions[0]?.id || "";
  const [primaryModel, setPrimaryModel] = useState(
    () => readScopedValue(storageScope, "primary_llm_model") || ""
  );
  const selectedPrimaryModel = isQueryModelSelectionEnabled
    ? primaryModel || defaultPrimaryModel
    : "";

  useEffect(() => {
    writeScopedValue(storageScope, "voice_lang", voiceLang);
  }, [storageScope, voiceLang]);

  useEffect(() => {
    if (!isQueryModelSelectionEnabled) {
      setPrimaryModel("");
      return;
    }
    const allowedModels = new Set(queryModelOptions.map((model) => model.id));
    if (!primaryModel || !allowedModels.has(primaryModel)) {
      setPrimaryModel(defaultPrimaryModel);
    }
  }, [defaultPrimaryModel, isQueryModelSelectionEnabled, primaryModel, queryModelOptions]);

  useEffect(() => {
    if (!isQueryModelSelectionEnabled || !selectedPrimaryModel) return;
    writeScopedValue(storageScope, "primary_llm_model", selectedPrimaryModel);
  }, [isQueryModelSelectionEnabled, selectedPrimaryModel, storageScope]);

  useEffect(() => {
    if (isTransitioning) {
      const timer = setTimeout(() => setIsTransitioning(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isTransitioning]);

  useEffect(() => {
    if (!isLoading) {
      setThinkingMessage(WORKFLOW_MESSAGES[0]);
      setIsThinkingMessageVisible(true);
      return;
    }

    let tick = 0;
    setThinkingMessage(WORKFLOW_MESSAGES[0]);
    setIsThinkingMessageVisible(true);

    const transitionToMessage = (nextMessage) => {
      setIsThinkingMessageVisible(false);
      if (thinkingFadeTimeoutRef.current) {
        clearTimeout(thinkingFadeTimeoutRef.current);
      }
      thinkingFadeTimeoutRef.current = setTimeout(() => {
        setThinkingMessage(nextMessage);
        setIsThinkingMessageVisible(true);
      }, 220);
    };

    const interval = setInterval(() => {
      tick += 1;

      if (tick < WORKFLOW_MESSAGES.length) {
        transitionToMessage(WORKFLOW_MESSAGES[tick]);
        return;
      }

      const extendedIndex = (tick - WORKFLOW_MESSAGES.length) % EXTENDED_MESSAGES.length;
      transitionToMessage(EXTENDED_MESSAGES[extendedIndex]);
    }, THINKING_MESSAGE_INTERVAL_MS);

    return () => {
      clearInterval(interval);
      if (thinkingFadeTimeoutRef.current) {
        clearTimeout(thinkingFadeTimeoutRef.current);
        thinkingFadeTimeoutRef.current = null;
      }
    };
  }, [isLoading]);

  useEffect(() => {
    if (!sourceDbConfigId) {
      setKydData(null);
      return;
    }
    setKydData(kydCache[sourceDbConfigId] || null);
  }, [sourceDbConfigId, kydCache]);

  useEffect(() => {
    if (location.pathname !== "/chatwindow") return;
    // Only show the wizard when both: a DB is selected AND that specific DB
    // is not yet configured. Switching to a configured DB dismisses the wizard.
    if (sourceDbConfigId && isSetupIncomplete) {
      setShowSetupModal(true);
    } else {
      setShowSetupModal(false);
    }
  }, [isSetupIncomplete, sourceDbConfigId, location.pathname]);

  useEffect(() => {
    let isActive = true;

    const fetchSuggestions = async () => {
      if (!sourceDbConfigId) {
        if (!isActive) return;
        setSuggestions([]);
        setSuggestionsEnabled(false);
        setSuggestionsLoading(false);
        return;
      }

      setSuggestionsLoading(true);
      setSuggestions([]);
      setSuggestionsEnabled(false);

      try {
        const resp = await api.get("/suggestions", {
          params: { source_db_config_id: sourceDbConfigId },
        });
        if (!isActive) return;
        if (resp.data?.enabled && Array.isArray(resp.data.suggestions)) {
          setSuggestions(resp.data.suggestions);
          setSuggestionsEnabled(true);
        } else {
          setSuggestions([]);
          setSuggestionsEnabled(false);
        }
      } catch (err) {
        if (!isActive) return;
        console.error("Failed to load suggestions", err);
        setSuggestions([]);
        setSuggestionsEnabled(false);
      } finally {
        if (isActive) {
          setSuggestionsLoading(false);
        }
      }
    };
    fetchSuggestions();
    return () => {
      isActive = false;
    };
  }, [sourceDbConfigId]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
    }
  }, [inputValue]);

  useEffect(() => {
    const chatWindow = chatWindowRef.current;
    if (!chatWindow) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = chatWindow;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      isNearBottomRef.current = isNearBottom;
      setShowScrollButton(!isNearBottom);
    };

    const handleManualScrollIntent = () => {
      if (liveRevealActiveRef.current) {
        manualStreamScrollOverrideRef.current = true;
      }
    };

    handleScroll();
    chatWindow.addEventListener("scroll", handleScroll);
    chatWindow.addEventListener("wheel", handleManualScrollIntent, { passive: true });
    chatWindow.addEventListener("touchmove", handleManualScrollIntent, { passive: true });
    chatWindow.addEventListener("pointerdown", handleManualScrollIntent, { passive: true });
    return () => {
      chatWindow.removeEventListener("scroll", handleScroll);
      chatWindow.removeEventListener("wheel", handleManualScrollIntent);
      chatWindow.removeEventListener("touchmove", handleManualScrollIntent);
      chatWindow.removeEventListener("pointerdown", handleManualScrollIntent);
    };
  }, []);

  useEffect(() => () => {
    clearLiveStreamTimers();
    if (scrollFrameRef.current) {
      window.cancelAnimationFrame(scrollFrameRef.current);
      scrollFrameRef.current = null;
    }
    if (sseReaderRef.current) {
      sseReaderRef.current.cancel().catch(() => {});
      sseReaderRef.current = null;
    }
    Object.values(insightReadersByBaseResponseIdRef.current).forEach((reader) => {
      reader?.cancel?.().catch(() => {});
    });
    insightReadersByBaseResponseIdRef.current = {};
  }, [clearLiveStreamTimers]);

  useEffect(() => {
    clearLiveStreamTimers();
    setLiveStreamStateByMessageId({});
  }, [sessionId, clearLiveStreamTimers]);

  const handleCSVDownload = async (resultId) => {
    if (!canDownload) {
      toast.error(DOWNLOAD_DENIED_TOOLTIP);
      return;
    }
    try {
      const loadingToast = toast.loading("Preparing download...");
      
      // Request Blob from Backend (Auth headers handled by 'api' instance)
      const response = await api.get(`/download/${resultId}`, {
        responseType: 'blob', 
      });
      
      // Create Object URL
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Set Filename
      const timestamp = new Date().toISOString().slice(0,10);
      link.setAttribute('download', `geneilytics_export_${timestamp}.csv`);
      
      // Trigger Click
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.dismiss(loadingToast);
      toast.success("Download started!");
    } catch (error) {
      console.error("Download failed", error);
      toast.dismiss();
      toast.error("Failed to download CSV. The file may have expired.");
    }
  };

  

  // ✅ Download Handler (Fixed Version)
  const handleDownload = useCallback(async (message) => {
    if (!canDownload) {
      toast.error(DOWNLOAD_DENIED_TOOLTIP);
      return;
    }
    if (downloadingIds[message.id]) return;
    setDownloadingIds((prev) => ({ ...prev, [message.id]: true }));
    try {
      if (message.responseId) {
        await downloadResponseAsPDF(message.responseId, `report_${message.id}.pdf`);
      } else {
        await downloadBubbleAsPDF(
          document.getElementById(`main-content-${message.id}`),
          `response_${message.id}.pdf`
        );
      }
    } catch (error) {
      console.error("Download failed", error);
    } finally {
      setDownloadingIds((prev) => ({ ...prev, [message.id]: false }));
    }
  }, [canDownload, downloadingIds]);

  const scrollToBottom = useCallback((behavior = "smooth") => {
    const scrollBehavior = normalizeScrollBehavior(behavior);

    if (scrollFrameRef.current) {
      window.cancelAnimationFrame(scrollFrameRef.current);
    }

    scrollFrameRef.current = window.requestAnimationFrame(() => {
      scrollFrameRef.current = null;
      if (!chatWindowRef.current) return;

      chatWindowRef.current.scrollTo({
        top: chatWindowRef.current.scrollHeight,
        behavior: scrollBehavior,
      });
    });
  }, []);

  const scrollMessageToTop = useCallback((messageId, behavior = "smooth") => {
    const scrollBehavior = normalizeScrollBehavior(behavior);
    const container = chatWindowRef.current;
    const target = document.getElementById(`msg-${messageId}`);
    if (!container || !target) return;

    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const targetTop = container.scrollTop + (targetRect.top - containerRect.top) - 8;

    container.scrollTo({
      top: Math.max(0, targetTop),
      behavior: scrollBehavior,
    });
  }, []);

  const handleFeedback = useCallback(async (msgId, score, reason, comment) => {
  // Optimistic UI Update: Update the local messages state immediately
  setMessagesMap((prev) => {
    const newMap = { ...prev };
    const sessionMsgs = newMap[sessionId] || [];
    
    const msgIndex = sessionMsgs.findIndex(m => m.messageId === msgId); // Use messageId (DB ID)
    if (msgIndex !== -1) {
      // Create a shallow copy of the message and update feedback
      const updatedMsg = { 
        ...sessionMsgs[msgIndex], 
        feedback: { score, reason, comment } 
      };
      
      const newSessionMsgs = [...sessionMsgs];
      newSessionMsgs[msgIndex] = updatedMsg;
      newMap[sessionId] = newSessionMsgs;
    }
    return newMap;
  });

  // API Call
  try {
    await api.post("/feedback", {
      message_id: msgId,
      score: score,
      reason: reason,
      comment: comment
    });
    // Optional: toast.success("Feedback submitted");
  } catch (err) {
    console.error("Feedback failed", err);
    toast.error("Failed to submit feedback");
    // revert optimistic update if strict consistency needed
  }
}, [sessionId]); // removed messagesMap dep to avoid frequent re-renders

  const hasActiveLiveStream = useMemo(
    () => Object.values(liveStreamStateByMessageId).some((streamState) => !streamState.completed),
    [liveStreamStateByMessageId]
  );

  useEffect(() => {
    liveRevealActiveRef.current = isLoading || hasActiveLiveStream;

    if (!liveRevealActiveRef.current) {
      manualStreamScrollOverrideRef.current = false;
    }
  }, [hasActiveLiveStream, isLoading]);

  useEffect(() => {
    const prevLength = prevMessagesLengthRef.current;
    prevMessagesLengthRef.current = messages.length;

    if (pinnedUserMessageIdRef.current && !manualStreamScrollOverrideRef.current) {
      scrollMessageToTop(pinnedUserMessageIdRef.current, "auto");
      return;
    }

    // ✅ FIX: Only auto-scroll if a NEW message was added or if the bot is thinking.
    // This prevents the scroll jump when updating feedback or adding knowledge instructions.
    if (
      !manualStreamScrollOverrideRef.current &&
      (messages.length > prevLength || isLoading) &&
      (isNearBottomRef.current || pinnedUserMessageIdRef.current)
    ) {
      scrollToBottom(isLoading ? "auto" : "smooth");
    }
  }, [messages, sessionId, isLoading, scrollMessageToTop, scrollToBottom]);

  useEffect(() => {
    if (!pinnedUserMessageIdRef.current) return;

    if ((isLoading || hasActiveLiveStream) && !manualStreamScrollOverrideRef.current) {
      scrollMessageToTop(pinnedUserMessageIdRef.current, "auto");
      return;
    }

    pinnedUserMessageIdRef.current = null;
  }, [hasActiveLiveStream, isLoading, liveStreamStateByMessageId, scrollMessageToTop]);

  const loadSessionMessages = useCallback(async (sessionId) => {
    try {
      setIsSessionLoading(true);
      const response = await api.get(`/sessions/${sessionId}/messages`);
      const { data } = response;
      const sourceDbHeader =
        response?.headers?.["x-source-db-id"] ??
        response?.headers?.["X-Source-DB-ID"];
      const parsedSourceDbId = Number(sourceDbHeader);
      if (Number.isFinite(parsedSourceDbId) && parsedSourceDbId > 0) {
        onSelectDatabase?.(parsedSourceDbId);
      }
      const processedMsgs = Array.isArray(data)
        ? data
            .map((m, index) => {
              const extras = m.extras || {};
              const clarificationFields = shapeAssistantClarificationFields(extras);
              const role = mapRole(m.role);
              const messageKind = extras.message_kind || null;
              const chart = extras.chart_data || null;
              const resultId = extras.result_id || null;
              const tableData = Array.isArray(extras.table_data) ? extras.table_data : [];
              const rawInsightStatus = m.insight_status ?? extras.insight_status ?? null;
              const isInsightEligible = inferInsightEligibility({
                role,
                messageKind,
                insightStatus: rawInsightStatus,
                sql: extras.sql,
                resultId,
                isDownloadable: extras.is_downloadable,
                chart,
              });
              const insight = m.insight || extras.insight || null;
              const insightStatus = normalizeInsightStatus(rawInsightStatus, {
                isEligible: isInsightEligible,
                insight,
              });
              return {
                id: m.id,
                messageId: m.id,
                role,
                status: extras.status || null,
                partial: Boolean(extras.partial),
                partialReason: extras.partial_reason || null,
                chartStatus: extras.chart_status || null,
                summaryStatus: extras.summary_status || null,
                content: m.text,
                responseId: m.response_id,
                feedback: m.feedback || null,
                sourceDbId: getSourceDbIdForMessage(
                  m.source_db_id ??
                  extras.source_db_id
                ),
                sql: extras.sql || null,
                sql_response: m.role === "assistant" ? m.text : null,
                insight,
                insightStatus,
                isInsightEligible,
                insightError: extras.insight_error || null,
                tableData,
                followUps: extras.follow_ups || [],
                chart,
                agentType: extras.agent_type || null,
                sources: typeof extras.sources === 'string' ? JSON.parse(extras.sources) : (extras.sources || null),

                // Capture from history and preserve deterministic turn order on reload.
                isDownloadable: extras.is_downloadable || false,
                downloadId: resultId,
                is_edited: m.is_edited || extras.is_edited || false,
                timestamp: new Date(m.created_at),
                _seq: index,
                ...clarificationFields,
              };
            })
            .sort((a, b) => {
              const aTs = a.timestamp instanceof Date ? a.timestamp.getTime() : 0;
              const bTs = b.timestamp instanceof Date ? b.timestamp.getTime() : 0;
              if (aTs !== bTs) return aTs - bTs;

              if (a.responseId && b.responseId && a.responseId === b.responseId && a.role !== b.role) {
                const roleOrder = { user: 0, bot: 1, system: 2 };
                return (roleOrder[a.role] ?? 99) - (roleOrder[b.role] ?? 99);
              }

              return a._seq - b._seq;
            })
            .map(({ _seq, ...msg }) => msg)
        : [];
      setMessagesMap((prev) => ({ ...prev, [sessionId]: processedMsgs }));
    } catch (error) {
      console.error("Error loading session messages:", error);
    } finally {
      setIsSessionLoading(false);
    }
  }, [getSourceDbIdForMessage, mapRole, onSelectDatabase]);

  useEffect(() => {
    if (!sessionId) return;
    if (sessionId.startsWith("temp_")) {
      setIsSessionLoading(false);
      return;
    }
    const msgs = messagesMap[sessionId];
    if (Array.isArray(msgs) && msgs.length > 0) {
      setIsSessionLoading(false);
      return;
    }
    loadSessionMessages(sessionId);
  }, [sessionId, loadSessionMessages]);

  useEffect(() => {
    if (!activeSessionId || activeSessionId === sessionId) return;
    if (activeSessionId?.startsWith("temp_")) {
      setSessionId(activeSessionId);
      return;
    }
    if (messagesMap[activeSessionId]) {
      setSessionId(activeSessionId);
    } else {
      setIsSessionLoading(true);
      setSessionId(activeSessionId);
    }
  }, [activeSessionId]);

  const translateToEnglish = async (text, voiceLang) => {
    if (!voiceLang || voiceLang.startsWith("en")) return text;

    
    try {
      const resp = await fetch("https://libretranslate.com/translate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ q: text, source: voiceLang.split("-")[0], target: "en", format: "text" }),
      });
      const data = await resp.json();
      return data?.translatedText || text;
    } catch (err) {
      console.error("Translation failed:", err);
      return text;
    }
  };

  const handleVoiceInput = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error("Voice input not supported in this browser");
      return;
    }
    if (listening && recognitionRef.current) {
      recognitionRef.current.stop();
      return;
    }
    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;
    recognition.lang = voiceLang;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    let finalTranscript = "";
    recognition.onstart = () => setListening(true);
    recognition.onresult = (event) => { finalTranscript = event.results[0][0].transcript; };
    recognition.onerror = (e) => {
      console.error("Speech error:", e.error);
      toast.error("Voice recognition failed");
      setListening(false);
    };
    recognition.onend = async () => {
      setListening(false);
      recognitionRef.current = null;
      const cleaned = finalTranscript.trim();
      if (!cleaned) return;
      const englishText = await translateToEnglish(cleaned, voiceLang);
      handleSendMessage(englishText);
    };
    recognition.start();
  };

  useEffect(() => {
    return () => { if (recognitionRef.current) recognitionRef.current.stop(); };
  }, []);

  const generateTitleFromText = (text) => {
    const cleaned = text.replace(/[\*_`#>]+/g, "").replace(/\n/g, " ").replace(/\s+/g, " ").trim();
    const stopWords = new Set(["the","a","an","and","or","but","in","on","at","to","for","of","with","by","is","are","was","were","be","being","have","has","had","do","does","did","will","would","could","should","may","might","can","shall","what","how","why","when","where","who","which","that","this","these","those","i","you","he","she","it","we","they","me","him","her","us","them"]);
    const keywords = cleaned.split(" ").filter(w => w.length > 2 && !stopWords.has(w.toLowerCase())).slice(0, 3);
    return keywords.length ? keywords.map(w => w[0].toUpperCase() + w.slice(1)).join(" ") : "New Chat";
  };

  // ✅ NEW: Handle Stop / Interrupt (Peer's Feature)
  const handleStop = async () => {
    if (!currentResponseId || !isInterruptibleRequest) return;

    // 1. Set flag so handleSendMessage knows to ignore the API error
    interruptedResponseIdsRef.current.add(currentResponseId);

    // 2. Optimistic UI Update: Show "Stopped" immediately
    setMessagesMap((prev) => ({
      ...prev,
      [sessionIdRef.current]: [
        ...(prev[sessionIdRef.current] || []),
        {
          id: Date.now() + Math.random(),
          messageId: null,
          role: "bot",
          responseId: currentResponseId,
          content: "Stopped. I've discarded that query. What shall we look at instead?",
          sql_response: "Stopped. I've discarded that query. What shall we look at instead?",
          insight: null,
          followUps: [],
          timestamp: new Date(),
          isError: true, // Optional styling flag
        },
      ],
    }));

    // 3. Unlock the UI immediately
    querySubmissionRef.current = null;
    setIsLoading(false);
    setCurrentResponseId(null);
    setActiveRequestKind(null);

    // 4. Send the interrupt signal in the background (Fire & Forget)
    if (sseReaderRef.current) {
      sseReaderRef.current.cancel().catch(() => {});
      sseReaderRef.current = null;
    }
    queryJobResponseIdsRef.current.delete(currentResponseId);
    queryJobTerminalResponseIdsRef.current.add(currentResponseId);

    try {
      await api.post("/interrupt", { response_id: currentResponseId });
    } catch (err) {
      console.error("Failed to send interrupt signal:", err);
    }
  };

  const renderQueryStreamUnavailable = useCallback((targetSessionId, responseId, fallbackSourceDbId) => {
    const errorMessage = mergeQueryStreamErrorPayload(
      {
        id: Date.now() + Math.random(),
        role: "bot",
        responseId,
        timestamp: new Date(),
        sourceDbId: getSourceDbIdForMessage(fallbackSourceDbId),
      },
      {
        response_id: responseId,
        status: "stream_unavailable",
        error: "Unable to stream the query result. Please retry.",
      }
    );

    setMessagesMap((prev) => ({
      ...prev,
      [targetSessionId]: appendAssistantMessageIfMissing(
        prev[targetSessionId] || [],
        errorMessage
      ),
    }));
    if (querySubmissionRef.current === responseId) {
      querySubmissionRef.current = null;
      setIsLoading(false);
      setCurrentResponseId(null);
      setActiveRequestKind(null);
    }
  }, [getSourceDbIdForMessage]);

  const openQueryEventStream = useCallback((responseId, sessionId, fallbackSourceDbId, faqLifecycle = {}) => {
    const token = localStorage.getItem("access_token");
    const baseURL = api.defaults.baseURL || "";
    const url = `${baseURL}query/${responseId}/events`;

    const failStream = () => {
      if (queryJobTerminalResponseIdsRef.current.has(responseId)) return;
      const delivery = advanceQueryDelivery(responseId, { type: "stream_failed" });
      if (shouldRenderStreamFailure(delivery)) {
        renderQueryStreamUnavailable(sessionId, responseId, fallbackSourceDbId);
      }
      clearSettledQueryDelivery(responseId, delivery);
    };

    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (response) => {
        if (
          queryJobTerminalResponseIdsRef.current.has(responseId) ||
          queryDeliveryByResponseIdRef.current[responseId]?.postMode === "final"
        ) {
          response.body?.cancel?.().catch(() => {});
          return;
        }
        if (!response.ok || response.status === 503 || !response.body) {
          failStream();
          return;
        }

        advanceQueryDelivery(responseId, { type: "stream_connected" });

        const reader = response.body.getReader();
        sseReaderRef.current = reader;
        const decoder = new TextDecoder();
        let buffer = "";

        let streamClosed = false;

        const closeStream = () => {
          streamClosed = true;
          reader.cancel().catch(() => {});
          if (sseReaderRef.current === reader) sseReaderRef.current = null;
        };

        const finishQueryRequest = (terminalResponseId = responseId) => {
          const delivery = advanceQueryDelivery(responseId, { type: "stream_terminal" });
          queryJobTerminalResponseIdsRef.current.add(terminalResponseId);
          queryJobTerminalResponseIdsRef.current.add(responseId);
          queryJobResponseIdsRef.current.delete(terminalResponseId);
          queryJobResponseIdsRef.current.delete(responseId);
          clearSettledQueryDelivery(responseId, delivery);
          if (querySubmissionRef.current === responseId) {
            querySubmissionRef.current = null;
            setIsLoading(false);
            setCurrentResponseId(null);
            setActiveRequestKind(null);
          }
        };

        const emitQuotaUpdateIfNeeded = (payload = {}) => {
          const status = String(payload.status || "").trim().toLowerCase();
          if (status !== "quota_exhausted" || typeof window === "undefined") return;

          const quota = extractQueryQuotaPayload(payload);
          window.dispatchEvent(
            new CustomEvent("geneilytics:quota-updated", {
              detail: quota,
            })
          );
        };

        const renderTerminalStreamError = (payload = {}) => {
          const targetResponseId = payload.response_id || responseId;
          const errorPayload = {
            ...payload,
            response_id: targetResponseId,
            status: payload.status || "error",
          };

          setMessagesMap((prev) => {
            const sessionMsgs = prev[sessionId] || [];
            let didUpdateBot = false;
            const msgs = sessionMsgs.map((message) => {
              if (message.role !== "bot" || message.responseId !== targetResponseId) {
                return message;
              }
              didUpdateBot = true;
              return mergeQueryStreamErrorPayload(message, errorPayload);
            });
            if (didUpdateBot) {
              return { ...prev, [sessionId]: msgs };
            }

            const errorMessage = mergeQueryStreamErrorPayload(
              {
                id: Date.now() + Math.random(),
                role: "bot",
                responseId: targetResponseId,
                timestamp: new Date(),
                sourceDbId: getSourceDbIdForMessage(fallbackSourceDbId),
              },
              errorPayload
            );

            return {
              ...prev,
              [sessionId]: [
                ...msgs,
                errorMessage,
              ],
            };
          });

          emitQuotaUpdateIfNeeded(errorPayload);
        };

        const mergeTerminalStreamPayload = (payload = {}) => {
          setMessagesMap((prev) => {
            const sessionMsgs = prev[sessionId] || [];
            let didUpdateBot = false;
            let didUpdateUser = false;

            const msgs = sessionMsgs.map((message) => {
              if (message.role === "bot" && message.responseId === responseId) {
                didUpdateBot = true;
                return mergeQueryTerminalPayload(message, payload);
              }

              if (
                payload.user_message_id &&
                message.role === "user" &&
                message.responseId === responseId &&
                !message.messageId
              ) {
                didUpdateUser = true;
                return { ...message, messageId: payload.user_message_id };
              }

              return message;
            });

            if (!didUpdateBot && !didUpdateUser) return prev;
            return { ...prev, [sessionId]: msgs };
          });
        };

        const dispatch = (eventName, data) => {
          if (streamClosed || queryJobTerminalResponseIdsRef.current.has(responseId)) return;

          if (eventName === "accepted") {
            faqLifecycle.onAdmitted?.();
            if (data?.status) {
              queryJobResponseIdsRef.current.add(responseId);
            }
          } else if (eventName === "core_result_ready") {
            faqLifecycle.onAdmitted?.();
            advanceQueryDelivery(responseId, { type: "stream_message" });
            const tableData = Array.isArray(data.table_data) ? data.table_data : [];
            const partialMsg = {
              id: Date.now() + Math.random(),
              role: "bot",
              ssePhase: "table_only",
              responseId,
              tableData,
              sql: data.sql || null,
              sql_response: null,
              chart: null,
              followUps: [],
              content: "",
              insight: null,
              insightStatus: "not_requested",
              isInsightEligible: false,
              partial: false,
              chartStatus: null,
              summaryStatus: null,
              isDownloadable: false,
              downloadId: data.result_id || null,
              sources: null,
              agentType: null,
              timestamp: new Date(),
              sourceDbId: getSourceDbIdForMessage(fallbackSourceDbId),
            };
            setMessagesMap((prev) => ({
              ...prev,
              [sessionId]: appendAssistantMessageIfMissing(
                prev[sessionId] || [],
                partialMsg
              ),
            }));
            if (!faqLifecycle.isFaq && !queryJobResponseIdsRef.current.has(responseId)) {
              setIsLoading(false);
            }
          } else if (eventName === "summary_ready") {
            const summaryText = data.summary || "";
            let msgForStream = null;
            setMessagesMap((prev) => {
              const msgs = (prev[sessionId] || []).map((m) => {
                if (m.responseId !== responseId || m.role !== "bot") return m;
                const updated = { ...m, sql_response: summaryText, content: summaryText, ssePhase: "summary_ready" };
                msgForStream = updated;
                return updated;
              });
              return { ...prev, [sessionId]: msgs };
            });
            if (msgForStream) {
              startLiveStream(msgForStream, { sectionKinds: ["text"] });
            }
          } else if (eventName === "chart_ready") {
            setMessagesMap((prev) => {
              const msgs = (prev[sessionId] || []).map((m) =>
                m.responseId === responseId && m.role === "bot"
                  ? { ...m, chart: data.chart_data || null, ssePhase: "chart_ready" }
                  : m
              );
              return { ...prev, [sessionId]: msgs };
            });
          } else if (eventName === "completed" || eventName === "partial_success") {
            mergeTerminalStreamPayload({
              ...data,
              status: data?.status || eventName,
            });
            closeStream();
            finishQueryRequest();
          } else if (eventName === "error") {
            faqLifecycle.onError?.(data);
            closeStream();
            const targetResponseId = data?.response_id || responseId;
            advanceQueryDelivery(responseId, { type: "stream_message" });
            renderTerminalStreamError({
              ...data,
              response_id: targetResponseId,
              status: data?.status || "error",
            });
            finishQueryRequest(targetResponseId);
          } else if (eventName === "cancelled") {
            closeStream();
            advanceQueryDelivery(responseId, { type: "stream_message" });
            renderTerminalStreamError({
              ...data,
              response_id: data?.response_id || responseId,
              status: data?.status || "cancelled",
              error: "Stopped. I've discarded that query. What shall we look at instead?",
            });
            finishQueryRequest(data?.response_id || responseId);
          }
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          const blocks = buffer.split("\n\n");
          buffer = blocks.pop() ?? "";
          for (const block of blocks) {
            if (!block.trim() || block.startsWith(": ")) continue;
            let event = null;
            let dataStr = null;
            for (const line of block.split("\n")) {
              if (line.startsWith("event: ")) event = line.slice(7).trim();
              else if (line.startsWith("data: ")) dataStr = line.slice(6).trim();
            }
            if (event && dataStr) {
              try { dispatch(event, JSON.parse(dataStr)); } catch {}
            }
          }
        }

        if (!streamClosed) {
          failStream();
        }
      })
      .catch(failStream);
  }, [
    advanceQueryDelivery,
    api,
    clearSettledQueryDelivery,
    getSourceDbIdForMessage,
    renderQueryStreamUnavailable,
    setIsLoading,
    setMessagesMap,
    startLiveStream,
  ]);

  const executeQueryRequest = useCallback(async ({
    requestKind = "normal",
    overrideInput = "",
    selectedOption = null,
    options = {},
    optimisticUserContent = "",
  } = {}) => {
    if (isLoading || querySubmissionRef.current || (options.faq && isSessionLoading)) return;
    if (isSetupIncomplete) {
      setShowSetupModal(true);
      return;
    }

    const messageText = String(overrideInput || inputValue || "").trim();
    const requiresText = requestKind === "normal" || requestKind === "custom";
    if (requiresText && !messageText) return;
    if (requestKind === "selected_option" && !selectedOption) return;
    if (!sourceDbConfigId) {
      toast.error("Please select a database first.");
      return;
    }

    finalizeAllLiveStreams();
    manualStreamScrollOverrideRef.current = false;
    
    // Generate unique response_id
    const responseId = window.crypto?.randomUUID?.() || `req_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    querySubmissionRef.current = responseId;
    const isInterruptible = requestKind !== "dismiss";
    if (isInterruptible) {
      setCurrentResponseId(responseId);
    } else {
      setCurrentResponseId(null);
    }
    queryJobResponseIdsRef.current.delete(responseId);
    queryJobTerminalResponseIdsRef.current.delete(responseId);
    setActiveRequestKind(requestKind);

    const { editMessageId, dbMessageId } = options; 
    const requestSessionId = sessionIdRef.current;
    const clarificationMessageId = activeClarification?.messageId || null;
    const currentSessionMessages = messagesMap[requestSessionId] || [];
    const fallbackSourceDbId =
      currentSessionMessages.find((m) => m.sourceDbId !== null && m.sourceDbId !== undefined)?.sourceDbId ??
      sourceDbConfigId ??
      user?.source_db_config_id ??
      null;
    
    if (requestKind === "normal" && messages.length <= 1 && !currentTitle) {
      const title = generateTitleFromText(messageText);
      setCurrentTitle(title);
    }

    if (requestKind !== "normal") {
      suppressClarificationForSession(requestSessionId, clarificationMessageId);
    }

    // --- OPTIMISTIC UI INJECTION ---
    let userMessage;
    if (editMessageId) {
      // Editing an existing message
      setMessagesMap((prev) => {
        const sessionMsgs = prev[requestSessionId] || [];
        const msgIndex = sessionMsgs.findIndex(m => m.id === editMessageId);
        if (msgIndex === -1) return prev;

        const oldResponseId = sessionMsgs[msgIndex].responseId;

        // Filter out the specific old bot response
        let newMsgs = sessionMsgs.filter(m => !(m.role === "bot" && m.responseId === oldResponseId));

        // Update the user message
        const newMsgIndex = newMsgs.findIndex(m => m.id === editMessageId);
        if (newMsgIndex !== -1) {
          newMsgs[newMsgIndex] = {
            ...newMsgs[newMsgIndex],
            content: messageText,
            responseId: responseId, // Critical: give it the new response ID
            is_edited: true
          };
          userMessage = newMsgs[newMsgIndex]; // Grab ref for scrolling
        }
        return { ...prev, [requestSessionId]: newMsgs };
      });
    } else if (optimisticUserContent) {
      // Brand new message
      userMessage = {
        id: Date.now() + Math.random(),
        role: "user",
        content: optimisticUserContent,
        responseId: responseId, 
        timestamp: new Date(),
      };
      setMessagesMap((prev) => ({
        ...prev,
        [requestSessionId]: [...(prev[requestSessionId] || []), userMessage],
      }));
    }

    // Trigger Scroll
    if (userMessage) {
        pinnedUserMessageIdRef.current = userMessage.id;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            scrollMessageToTop(userMessage.id, "smooth");
          });
        });
    }
    // ----------------------------------------

    window.speechSynthesis?.cancel();
    setInputValue("");
    setIsLoading(true);
    let actualSessionId = requestSessionId;
    let shouldFinalizeRequest = true;
    let faqAdmitted = false;
    const admitFaq = () => {
      if (!options.faq || faqAdmitted) return;
      faqAdmitted = true;
      setMessagesMap(prev => ({
        ...prev,
        [actualSessionId]: markClarificationAsHandled(prev[actualSessionId] || [], clarificationMessageId, "cancelled"),
      }));
    };

    const handleFaqError = (payload = {}) => {
      if (!options.faq) return;
      if (payload.status === "faq_execution_failed") admitFaq();
      if (["faq_stale", "faq_unavailable"].includes(payload.status)) {
        setFaqRefreshBySource(prev => ({ ...prev, [sourceDbConfigId]: (prev[sourceDbConfigId] || 0) + 1 }));
      }
    };

    try {
      // --- Session Creation Logic ---
      if (requestKind === "normal" && requestSessionId.startsWith("temp_")) {
        const resolvedTitle = currentTitle || generateTitleFromText(messageText);
        const { data: newSession } = await api.post("/sessions", {
          title: resolvedTitle,
          source_db_config_id: sourceDbConfigId,
        });
        if (interruptedResponseIdsRef.current.has(responseId)) return;
        actualSessionId = newSession.id;
        setCurrentTitle(newSession.title || resolvedTitle);
        setMessagesMap((prev) => ({ ...prev, [actualSessionId]: prev[requestSessionId] || [] }));
        setSessionId(actualSessionId);
        onActiveSessionChange?.(actualSessionId);
        setActiveSession(newSession);
        onSessionCreated?.();
        setTimeout(() => {
          setMessagesMap((prev) => {
            const copy = { ...prev };
            delete copy[requestSessionId];
            return copy;
          });
        }, 0);
      } else if (requestKind !== "normal" && requestSessionId.startsWith("temp_")) {
        throw new Error("Clarification actions require an existing chat session.");
      }

      // --- API Payload ---
      const payload = buildQueryPayload({
        mode: requestKind,
        messageText,
        sessionId: actualSessionId,
        responseId,
        sourceDbConfigId,
        selectedOption,
        dbMessageId,
        primaryModel: selectedPrimaryModel,
        faq: options.faq,
      });

      // Open SSE stream concurrently with POST (SQL queries only)
      if (requestKind !== "dismiss") {
        queryDeliveryByResponseIdRef.current[responseId] = createQueryDeliveryState();
        openQueryEventStream(responseId, actualSessionId, fallbackSourceDbId, { isFaq: Boolean(options.faq), onAdmitted: admitFaq, onError: handleFaqError });
      }

      const queryResponse = await api.post("/query", payload);
      if (interruptedResponseIdsRef.current.has(responseId)) return;
      const { data } = queryResponse;
      admitFaq();

      if (data.user_message_id && userMessage) {
        setMessagesMap((prev) => {
          const sessionMsgs = [...(prev[actualSessionId] || [])];
          const uIdx = sessionMsgs.findIndex((m) => m.id === userMessage.id);
          if (uIdx !== -1) {
            sessionMsgs[uIdx] = { ...sessionMsgs[uIdx], messageId: data.user_message_id };
          }
          return { ...prev, [actualSessionId]: sessionMsgs };
        });
      }

      const queryResponseKind = classifyQueryResponse(queryResponse);
      if (queryResponseKind.mode === "accepted") {
        const acceptedResponseId = queryResponseKind.responseId || responseId;
        const delivery = advanceQueryDelivery(responseId, {
          type: "post_settled",
          mode: "accepted",
        });

        if (shouldRenderStreamFailure(delivery)) {
          renderQueryStreamUnavailable(actualSessionId, responseId, fallbackSourceDbId);
          clearSettledQueryDelivery(responseId, delivery);
          return;
        }

        if (
          queryResponseKind.terminal ||
          queryJobTerminalResponseIdsRef.current.has(acceptedResponseId)
        ) {
          queryJobResponseIdsRef.current.delete(acceptedResponseId);
        } else {
          queryJobResponseIdsRef.current.add(acceptedResponseId);
          shouldFinalizeRequest = false;
        }
        clearSettledQueryDelivery(responseId, delivery);
        return;
      }

      if (requestKind === "dismiss") {
        setMessagesMap((prev) => ({
          ...prev,
          [actualSessionId]: markClarificationAsHandled(
            prev[actualSessionId] || [],
            activeClarification?.messageId,
            data.clarification_status || "cancelled"
          ),
        }));
        return;
      }

      const delivery = advanceQueryDelivery(responseId, {
        type: "post_settled",
        mode: "final",
      });

      // SSE owns the rendered response once it has produced visible content.
      if (!shouldRenderPostResult(delivery)) {
        clearSettledQueryDelivery(responseId, delivery);
        return;
      }

      // Non-SSE fallback: clarification, general chat, or SSE 503/unavailable
      const newAssistantMessage = buildAssistantMessageFromResponse(
        data,
        responseId,
        fallbackSourceDbId
      );

      setMessagesMap((prev) => ({
        ...prev,
        [actualSessionId]: appendAssistantMessageIfMissing(
          prev[actualSessionId] || [],
          newAssistantMessage
        ),
      }));
      startLiveStream(newAssistantMessage);
      clearSettledQueryDelivery(responseId, delivery);

    } catch (error) {
      console.error("Error communicating with backend:", error);
      if (!interruptedResponseIdsRef.current.has(responseId)) handleFaqError(error.response?.data);
      const delivery = requestKind !== "dismiss"
        ? advanceQueryDelivery(responseId, { type: "post_settled", mode: "final" })
        : null;

      // 1. Check if this was manually stopped by the user
      if (interruptedResponseIdsRef.current.has(responseId)) {

         if (requestKind !== "normal") {
           restoreClarificationForSession(actualSessionId, clarificationMessageId);
         }

         const interruptedMessageId = error.response?.data?.message_id || null;
         
         if (interruptedMessageId) {
           setMessagesMap((prev) => {
             const updated = [...(prev[actualSessionId] || [])];
             const idx = updated.findIndex(
               (m) => m.role === "bot" && m.responseId === responseId && !m.messageId
             );
             if (idx !== -1) {
               updated[idx] = { ...updated[idx], messageId: interruptedMessageId };
               return { ...prev, [actualSessionId]: updated };
             }
             return prev;
           });
         }
         return; 
      }

      // 2. Handle Actual Errors (Excluding 499 Interrupts)
      if (error.response?.status !== 499) {
          const errorMessage =
            getLlmProviderErrorMessage(error) ||
            getQueryErrorMessage(error);

          if (requestKind === "selected_option" || requestKind === "custom") {
            if (userMessage) {
              setMessagesMap((prev) => ({
                ...prev,
                [actualSessionId]: (prev[actualSessionId] || []).filter(
                  (message) => message.id !== userMessage.id
                ),
              }));
            }
            restoreClarificationForSession(actualSessionId, clarificationMessageId);
            toast.error(errorMessage);
          } else if (requestKind === "dismiss") {
            restoreClarificationForSession(actualSessionId, clarificationMessageId);
            toast.error(errorMessage);
          } else if (delivery?.hasStreamMessage) {
            // The SSE channel already owns this response; ignore the POST failure.
          } else {
            setMessagesMap((prev) => ({
              ...prev,
              [actualSessionId]: appendAssistantMessageIfMissing(
                prev[actualSessionId] || [],
                {
                  id: Date.now(),
                  role: "bot",
                  responseId,
                  content: errorMessage,
                  timestamp: new Date(),
                  isError: true, 
                }
              ),
            }));
          }
      }
      if (delivery) {
        clearSettledQueryDelivery(responseId, delivery);
      }
    } finally {
      interruptedResponseIdsRef.current.delete(responseId);
      if (shouldFinalizeRequest && querySubmissionRef.current === responseId) {
        querySubmissionRef.current = null;
        setIsLoading(false);
        setCurrentResponseId(null);
        setActiveRequestKind(null);
      }
    }
  }, [
    activeClarification?.messageId,
    advanceQueryDelivery,
    buildAssistantMessageFromResponse,
    clearSettledQueryDelivery,
    currentTitle,
    finalizeAllLiveStreams,
    inputValue,
    isLoading,
    isSessionLoading,
    isSetupIncomplete,
    markClarificationAsHandled,
    messages,
    messagesMap,
    onActiveSessionChange,
    onSessionCreated,
    openQueryEventStream,
    renderQueryStreamUnavailable,
    restoreClarificationForSession,
    selectedPrimaryModel,
    setSessionId,
    sourceDbConfigId,
    startLiveStream,
    suppressClarificationForSession,
    user,
  ]);

  const handleSendMessage = useCallback((overrideInput = "", options = {}) => {
    return executeQueryRequest({
      requestKind: "normal",
      overrideInput,
      options,
      optimisticUserContent: String(overrideInput || inputValue || "").trim(),
    });
  }, [executeQueryRequest, inputValue]);

  const handleSubmitCustomClarification = useCallback(() => {
    if (!allowCustomClarification) return;
    return executeQueryRequest({
      requestKind: "custom",
      optimisticUserContent: String(inputValue || "").trim(),
    });
  }, [allowCustomClarification, executeQueryRequest, inputValue]);

  const handleSelectClarificationOption = useCallback((selectedOption) => {
    if (!selectedOption) return;
    return executeQueryRequest({
      requestKind: "selected_option",
      selectedOption,
      optimisticUserContent: String(selectedOption.label || "").trim(),
    });
  }, [executeQueryRequest]);

  const handleDismissClarification = useCallback(() => {
    return executeQueryRequest({
      requestKind: "dismiss",
    });
  }, [executeQueryRequest]);
  
  const handleFollowUp = useCallback((question) => {
    handleSendMessage(question);
  }, [handleSendMessage]);

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (isClarificationMode) {
        handleSubmitCustomClarification();
        return;
      }
      handleSendMessage();
    }
  };

// Inside ChatWindow component...

const renderMarkdown = useCallback((text) => {
  const dirty = marked.parse(text || "");

  const moreIconSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="pointer-events: none;"><circle cx="12" cy="12" r="1"/><circle cx="12" cy="5" r="1"/><circle cx="12" cy="19" r="1"/></svg>`;

  const tableHeader = `
    <div class="table-wrapper-header">
      <span class="table-label"></span> <button class="table-options-trigger" aria-label="Table Options">
        ${moreIconSvg}
      </button>
    </div>
  `;

  // Inject wrapper. 
  // IMPORTANT: The key is that .table-scroll is DIRECTLY after header with no line breaks or gaps
  const withTableWrapper = dirty
    .replace(/<table>/g, `<div class="table-container">${tableHeader}<div class="table-scroll"><table>`)
    .replace(/<\/table>/g, "</table></div></div>");

  const clean = DOMPurify.sanitize(withTableWrapper, {
    ADD_TAGS: ['button', 'svg', 'circle', 'path'], 
    ADD_ATTR: ['class', 'viewBox', 'fill', 'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin', 'cx', 'cy', 'r', 'width', 'height', 'style', 'aria-label']
  });

  return { __html: clean };
}, []);


  const preloadKydData = async () => {
    if (!sourceDbConfigId) {
      toast.error("Please select a database first.");
      return;
    }

    if (kydCache[sourceDbConfigId]) {
      setKydData(kydCache[sourceDbConfigId]);
      return;
    }

    try {
      setKydLoading(true);
      const resp = await api.get("/kyd", {
        params: { source_db_config_id: sourceDbConfigId },
      });
      setKydData(resp.data);
      setKydCache((prev) => ({ ...prev, [sourceDbConfigId]: resp.data }));
    } catch (err) {
      console.error("Failed to preload KYD data", err);
    } finally {
      setKydLoading(false);
    }
  };

  const handleEditMessage = (localId, dbId, newText) => {
    handleSendMessage(newText, { editMessageId: localId, dbMessageId: dbId });
  };

  return (
    <div className="app">



      <div className={`chat-main ${sidebarCollapsed ? "sidebar-collapsed" : "sidebar-expanded"}`}>
        <div className="chat-area">
          <div className={`chat-body ${messages.length === 0 ? "empty" : ""}`} ref={chatWindowRef}>
            {isSessionLoading && <SkeletonLoader />}

            {!isSessionLoading && messages.length === 0 && (
              <div className="empty-state">
                <Greeting userName={user?.name} />
                {databases.length > 0 ? (
                <DatabaseSelector
                  variant="hero"
                  labelPrefix="You are currently querying:"
                  databases={databases}
                  selectedDatabaseId={selectedDatabaseId}
                  onChange={onSelectDatabase}
                />
              ) : (
                <DatabaseSelectorSkeleton variant="hero" />
              )}
                <div className="empty-suggestions-label"></div>
                <div className="empty-suggestions-row">
                  {suggestionsLoading
                    ? [0, 1].map((idx) => <SuggestionCardSkeleton key={`skeleton-${idx}`} />)
                    : suggestions.slice(0, 2).map((s, idx) => (
                        <SuggestionCard
                          key={idx}
                          title={s.title}
                          subtitle={s.subtitle}
                          onClick={() => {
                            const combined = [s.title, s.subtitle]
                              .filter(Boolean)
                              .join(" - ");
                            setTimeout(() => handleSendMessage(combined), 80);
                          }}
                        />
                      ))}
                </div>
                <div className="empty-divider"> </div>
              {/*}  <div className="empty-kyd-row">
                  <KydCard
                    onClick={() => {
                      preloadKydData();
                      setShowKydModal(true);
                    }}
                  />
                </div>
                */}
              </div>
            )}

            {!isSessionLoading && messages.length > 0 && (
              <div className={`chat-window ${isTransitioning ? "transitioning" : ""}`}>
                <div className="chat-messages">
                  <MessageList
                    messages={messages}
                    Logo={Logo}
                    user={user}
                   // UserAvatar={UserAvatar}
                    renderMarkdown={renderMarkdown}
                    handleFollowUp={handleFollowUp}
                    sidebarCollapsed={sidebarCollapsed}
                    //  Passed new props
                    downloadingIds={downloadingIds}
                    handleDownload={handleDownload} 
                    onCSVDownload={handleCSVDownload}
                    onFeedback={handleFeedback}
                    theme={theme}
                    canDownload={canDownload}
                    onAddInstruction={handleAddInstruction}
                    onRemoveInstruction={handleRemoveInstruction}
                    dbList={databases}
                    sourceDbConfigId={sourceDbConfigId}
                    isLoading={isLoading} // <--- NEW
                    handleEditMessage={handleEditMessage} // <--- NEW
                    liveStreamStateByMessageId={liveStreamStateByMessageId}
                    onGenerateInsight={handleGenerateInsight}
                  />

                  {isLoading && (
                    <div className="message bot-message thinking-message">
                      <img src={Logo} alt="Logo" className="avatar-img" />
                      <div className="bubble thinking">
                        <div className="thinking-card">
                          <div className="thinking-content">
                            <div className="thinking-title">Analyzing</div>
                            <div className={`thinking-line ${isThinkingMessageVisible ? "is-visible" : "is-fading"}`}>
                              {thinkingMessage}
                            </div>
                            <div className="thinking-track" aria-hidden="true">
                              <span className="thinking-track-bar" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {isLoading && <div className="query-anchor-spacer" aria-hidden="true" />}
                </div>
              </div>
            )}
          </div>
        </div>

        {messages.length > 0 && (
          <button className={`to-bottom ${showScrollButton ? "show" : ""}`} onClick={() => scrollToBottom()}>
            <ChevronDown size={20} />
          </button>
        )}

        <div className="footer-section">
          <div className={`composer ${composerActivityClass} ${isSessionLoading || isSetupIncomplete ? "composer-disabled" : ""} ${isClarificationMode ? "composer--clarification" : ""}`}>
            <div className={`composer-inner ${isClarificationMode ? "composer-inner--clarification" : ""}`}>
              {isClarificationMode ? (
                <div className="clarification-composer">
                  <div className="clarification-composer__header">
                    <div className="clarification-composer__eyebrow">Clarification needed</div>
                    <div className="clarification-composer__question">
                      {clarificationRequest?.question || visibleActiveClarification?.question}
                    </div>
                  </div>

                  <div className="clarification-composer__options" role="list">
                    {(clarificationRequest?.options || []).map((option, index) => {
                      const optionKey =
                        option.expression_id ??
                        option.label ??
                        `clarification-option-${index}`;

                      return (
                        <button
                          key={optionKey}
                          type="button"
                          className="clarification-option"
                          disabled={isLoading || isSessionLoading || isSetupIncomplete}
                          onClick={() => handleSelectClarificationOption(option)}
                        >
                          <span className="clarification-option__index">{index + 1}</span>
                          <span className="clarification-option__body">
                            <span className="clarification-option__label-row">
                              <span className="clarification-option__label">{option.label}</span>
                              {option.description ? (
                                <span
                                  className="clarification-option__info"
                                  tabIndex={0}
                                  aria-label={option.description}
                                >
                                  i
                                  <span className="clarification-option__tooltip" role="tooltip">
                                    {option.description}
                                  </span>
                                </span>
                              ) : null}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {allowCustomClarification ? (
                    <div className="clarification-composer__custom">
                      <label className="clarification-composer__custom-label" htmlFor="clarification-input">
                        {clarificationRequest?.custom_response_label || "Other"}
                      </label>
                      <textarea
                        id="clarification-input"
                        ref={textareaRef}
                        value={inputValue}
                        disabled={isLoading || isSessionLoading || isSetupIncomplete}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Type your own clarification..."
                        rows={1}
                      />
                    </div>
                  ) : (
                    <div className="clarification-composer__hint">
                      Choose one of the options above to continue.
                    </div>
                  )}

                  <div className="clarification-composer__footer">
                    {allowDismissClarification ? (
                      <button
                        type="button"
                        className="clarification-dismiss-btn"
                        disabled={isLoading || isSessionLoading || isSetupIncomplete}
                        onClick={handleDismissClarification}
                      >
                        {clarificationRequest?.dismiss_label || "Ask something else"}
                      </button>
                    ) : null}

                    {(allowCustomClarification || (isLoading && isInterruptibleRequest)) ? (
                      <button
                        className="send-btn clarification-send-btn"
                        disabled={
                          isSessionLoading ||
                          isSetupIncomplete ||
                          (!isLoading && !canSubmitCustomClarification)
                        }
                        onClick={
                          isLoading && isInterruptibleRequest
                            ? handleStop
                            : handleSubmitCustomClarification
                        }
                        title={
                          isLoading && isInterruptibleRequest
                            ? "Stop generating"
                            : "Send clarification"
                        }
                      >
                        {isLoading && isInterruptibleRequest ? (
                          <Square fill="white" size={16} />
                        ) : (
                          <Send size={18} />
                        )}
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : (
                <>
                  <textarea
                    id="user-input"
                    ref={textareaRef}
                    value={inputValue}
                    disabled={isLoading || isSessionLoading || isSetupIncomplete}
                    readOnly={listening}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={
                      isSetupIncomplete
                        ? "Complete the app configuration in settings before asking queries."
                        : isSessionLoading
                          ? "Loading conversation..."
                          : "Type your message here..."
                    }
                    rows={1}
                  />
                  {isQueryModelSelectionEnabled ? (
                    <select
                      className="voice-lang-select llm-model-select"
                      value={selectedPrimaryModel}
                      onChange={(e) => setPrimaryModel(e.target.value)}
                      disabled={listening || isSetupIncomplete}
                      aria-label="Primary LLM model"
                      title="Primary LLM model"
                    >
                      {queryModelOptions.map((model) => (
                        <option key={model.id} value={model.id}>
                          {model.label || model.id}
                        </option>
                      ))}
                    </select>
                  ) : null}
                  <select className="voice-lang-select" value={voiceLang} onChange={(e) => setVoiceLang(e.target.value)} disabled={listening || isSetupIncomplete}>
                    {VOICE_LANGUAGES.map((l) => (
                      <option key={l.code} value={l.code}>{l.label}</option>
                    ))}
                  </select>
                  <button className={`voice-btn ${listening ? "listening" : ""}`} disabled={isLoading || isSessionLoading || isSetupIncomplete} onClick={handleVoiceInput}>
                    {listening ? <span className="wave"><i /><i /><i /></span> : <Mic size={18} />}
                  </button>
                  <button 
                    className="send-btn"
                    disabled={
                      isSessionLoading ||
                      listening ||
                      isSetupIncomplete ||
                      (isLoading && !isInterruptibleRequest)
                    }
                    onClick={isLoading && isInterruptibleRequest ? handleStop : () => handleSendMessage()}
                    title={
                      isLoading && isInterruptibleRequest
                        ? "Stop generating"
                        : isLoading
                          ? "Processing request"
                          : "Send message"
                    }
                  >
                    {isLoading && isInterruptibleRequest ? <Square fill="white" size={16} /> : <Send size={18} />}
                  </button>
                </>
              )}
            </div>
          </div>
          <div className="legal">
            © 2026 ScatterPie Analytics Pvt. Ltd. • <a href="https://www.scatterpie.io/privacy-policy/">Privacy Policy</a>
          </div>
        </div>

        {/* ✅ FIX: Pass the new handleSaveInstruction function */}
        <InstructionModal 
          isOpen={instructionModalOpen}
          onClose={() => setInstructionModalOpen(false)}
          data={currentInstruction}
          onSave={handleSaveInstruction} // Use the new handler here
          loading={instructionLoading}
          dbList={databases}
        />

        {showKydModal && (
          <div className="kyd-modal-backdrop">
            <div className="kyd-modal">
              <KydPage kydData={kydData} loading={kydLoading} onClose={() => setShowKydModal(false)} />
            </div>
          </div>
        )}

        {showSetupModal && (
          <OnboardingWizard
            pendingSetupItems={pendingSetupItems}
            isTenantAdmin={isTenantAdmin}
            onClose={() => setShowSetupModal(false)}
            onRefreshPending={refreshCurrentUser}
            onNavigate={(path) => {
              setShowSetupModal(false);
              navigate(path);
            }}
          />
        )}

{String(selectedDatabase?.type || selectedDatabase?.db_type || "").toLowerCase() !== "tableau" && (
  <FaqPopover
    sourceDbConfigId={sourceDbConfigId}
    databaseName={selectedDatabase?.generic_name || selectedDatabase?.display_label || selectedDatabase?.name || ""}
    disabled={isLoading || Boolean(querySubmissionRef.current) || isSessionLoading || isSetupIncomplete}
    refreshKey={faqRefreshBySource[sourceDbConfigId] || 0}
    onRun={faq => handleSendMessage(faq.question_text, { faq })}
  />
)}
<button
  className="kyd-fab"
  title="Know Your Data"
  onClick={() => {
    if (!sourceDbConfigId) {
      toast.error("Please select a database first.");
      return;
    }
    preloadKydData();
    setShowKydModal(true);
  }}
>
  <Database size={20} />
</button>

        
      </div>
    </div>
  );
};

export default ChatWindow;
